import * as THREE from "three";
import { Engine } from "./Engine.js";
import { Input } from "./Input.js";
import { CameraRig } from "./CameraRig.js";
import { AssetRegistry } from "./AssetRegistry.js";
import { fitToHeight, instancedTiles } from "./modelFit.js";
import { LevelBuilder } from "../levels/LevelBuilder.js";
import { THEMES, pickTheme } from "../levels/themes.js";
import { generateArena } from "../levels/generators/arena.js";
import { Player } from "../entities/Player.js";
import { spawnEnemies, Enemy, ENEMY_DEFS } from "../entities/Enemy.js";
import { Turret } from "../entities/Turret.js";
import { abilityFor } from "../config/abilities.js";
import { RECIPES, recipeById } from "../config/crafting.js";
import { getCode } from "../config/codes.js";
import { Npc } from "../entities/Npc.js";
import { Pet } from "../entities/Pet.js";
import { PETS, getPet } from "../config/pets.js";
import { CharacterAnimator } from "../entities/CharacterAnimator.js";
import { CombatSystem } from "../systems/CombatSystem.js";
import { LootSystem, equipItem, useItem } from "../systems/LootSystem.js";
import { rollLoot, rollChest, rollChestSlots } from "../systems/LootTables.js";
import { Effects } from "../systems/Effects.js";
import { HUD } from "../ui/HUD.js";
import { Minimap } from "../ui/Minimap.js";
import { ItemIcons } from "../ui/ItemIcons.js";
import { createPortal, updatePortal } from "../world/Portal.js";
import { AudioManager } from "./AudioManager.js";
import { SaveManager, RUN_VERSION } from "./SaveManager.js";
import { GAME, TILE, DIFFICULTIES, DEFAULT_DIFFICULTY } from "../config/constants.js";
import { MANIFEST } from "../config/assetManifest.js";
import { getCharacter, DEFAULT_CHARACTER, CHARACTERS } from "../config/characters.js";
import { FEATURES } from "../config/features.js";
import { starterKit, makeShopStock, FOOD_TINTS, priceFor, rehydrateItem, makeMemoryRelic, makeKey } from "../items/ItemFactory.js";
import { HELIX_SECTIONS, memoriesForSection, sectionTier, HELIX_TIERS } from "../config/helix.js";
import { achievementsFor, achieveProgress, allEarned, unlockedSkins, skinById, SKINS } from "../config/achievements.js";
import { recordKill, recordDepth, recordSave, memTier } from "../systems/RelicMemory.js";
import { rollNamedRelic, makeNamedRelic, NAMED_RELICS } from "../config/relics.js";
import { rollMemory, makeMemoryItem, getMemory, getCodex, codexComplete, completedCodexCount, totalCollected, TOTAL_MEMORIES, CODEX_COUNT as MEM_CODEX_COUNT } from "../config/memories.js";
import { getResearch, RESEARCH } from "../config/research.js";
import { clamp, makeRng } from "./util.js";
import { BananaScare } from "../systems/BananaScare.js";
import { addItem, canAdd, takeOne, stackCount, padBag, slotCount } from "../systems/Inventory.js";
import { buildRecap } from "../systems/Recap.js";
import { Hazards } from "../systems/Hazards.js";
import { A11Y } from "./a11y.js";

/**
 * Game orchestrates every subsystem and runs the state machine
 * (loading → menu → playing → dead) plus the frame loop. The menu shows a live
 * 3D showcase (hero vs skeletons, torch-lit) behind the landing page.
 */
export class Game {
  constructor(canvas) {
    this.engine = new Engine(canvas);
    this.input = new Input(canvas);
    this.cameraRig = new CameraRig(this.engine.aspect);
    this.registry = new AssetRegistry();
    this.combat = new CombatSystem(this.engine.scene);
    this.loot = new LootSystem(this.engine.scene, this.registry);
    this.effects = new Effects(this.engine.scene, this.cameraRig);
    this.levelBuilder = new LevelBuilder(this.engine.scene, this.registry);
    this.hud = new HUD(this);
    this.minimap = new Minimap();
    this.icons = new ItemIcons(this.registry);
    this.audio = new AudioManager();
    // gag jump scare: fires when a banana hits the ground (opt-in, off by default)
    this.bananaScare = new BananaScare(this.audio);
    this.loot.onDrop = (item) => this.bananaScare.maybeScare(item);

    this.state = "loading";
    this.floor = 1;
    this.totalKills = 0;
    this.player = null;
    this.enemies = [];
    this.turrets = [];              // live Ranger sentry turrets
    this.portal = null;
    this.theme = THEMES.dungeon;
    this._prevThemeId = null;
    this.difficulty = DIFFICULTIES.find((d) => d.id === DEFAULT_DIFFICULTY) || DIFFICULTIES[1];
    this.character = getCharacter(DEFAULT_CHARACTER);   // chosen hero
    this.save = new SaveManager();  // local per-character save (level/XP)
    this.meta = this.save.meta();   // global Vault meta (notoriety / shades / codex)
    this.profile = null;            // active character profile
    this.npcs = [];                 // hub NPCs
    this.inHub = false;             // walking around the Waystation
    this.hubPanelOpen = false;      // an NPC panel is open (freezes movement)
    this.nearNpc = null;
    this.godMode = false;           // debug: player can't die
    this.debugBiome = null;         // debug: force the next level's biome
    this.showcase = null;
    this.menuAnimators = [];

    this.engine.onResize = (a) => this.cameraRig.setAspect(a);
    this.combat.effects = this.effects;
    this.combat.onEnemyKilled = (e) => this._onEnemyKilled(e);
    this.combat.onPlayerDeath = () => this._onPlayerDeath();
    this.combat.onDestructibleBroken = (d) => this._onBreak(d);

    // [Esc] closes whatever is open, in priority order: an open Waystation
    // station panel, then the inventory, else it pauses/resumes the run. (Light
    // DOM modals like Settings/Feats are handled first by a capture-phase
    // listener in main.js, which stops propagation before this runs.)
    window.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (this._recapOpen) return;   // the recap is dismissed with its own button
      if (this.hubPanelOpen) { e.preventDefault(); this.closeStation(); return; }
      if (this.hud && this.hud.inventoryOpen) { e.preventDefault(); this.hud.closeInventory(); return; }
      if (this.state === "playing" || this.state === "paused") { e.preventDefault(); this.togglePause(); }
    });
  }

  async init() {
    this.hud.showLoading(true);
    this.registry.register(MANIFEST);
    await this.registry.loadAll();
    this._applyHexAtlas();   // the converted hex models share one texture atlas
    this._applyFoodColors(); // the converted food models get a solid tint each
    const miss = this.registry.missing();
    this.hud.setAssetNote(this.registry.loadedCount(), this.registry.totalCount(), miss);
    if (miss.length) console.warn(
      `[Relikara] ${miss.length} asset file(s) failed to load — almost always because they are still "cloud-only" in OneDrive (not downloaded to disk). In File Explorer: right-click public/assets → "Always keep on this device", wait for green checks, then hard-refresh.\nMissing keys:\n  ` + miss.join("\n  "));
    this.hud.showLoading(false);
    this.icons.prewarm([
      "weapon/sword", "weapon/sword2", "weapon/axe", "weapon/axe2", "weapon/dagger", "weapon/bow", "weapon/crossbow",
      "weapon/staff", "weapon/wand", "weapon/shield", "weapon/shield_square", "weapon/shield_spikes",
      "potion/health", "potion/greater", "dungeon/coins", "resource/copper_nugget", "resource/copper_bar",
      "relic/key_gold",   // Vault Key — crafting output + chest-slot icon
    ]);
    // posed hero portraits (idle stance + weapon) for the character-select cards
    for (const c of CHARACTERS) this.icons.renderCharacter(c.model, starterKit(c.id).weapon.model);
    this._buildShowcase();
    this.hud.showStart();
    this.audio.play("menu");
    this.state = "menu";
    this._loop();
  }

  /** The hexagon-pack models were converted from FBX with a broken texture ref;
   *  they all share one UV atlas — load it once and assign it to every hex model. */
  _applyHexAtlas() {
    try {
      const tex = new THREE.TextureLoader().load("assets/hex/atlas.png");
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
      for (const key in this.registry.models) {
        if (!key.startsWith("hex/")) continue;
        this.registry.models[key].traverse((o) => {
          if (!o.isMesh || !o.material) return;
          const mats = Array.isArray(o.material) ? o.material : [o.material];
          for (const m of mats) { m.map = tex; m.metalness = 0; m.roughness = 1; m.needsUpdate = true; }
        });
      }
    } catch (_) { /* atlas missing → models render untextured */ }
  }

  /** The food-kit glbs converted with a broken 1×1 texture; strip it and paint
   *  each food model its own solid low-poly colour (see FOOD_TINTS). */
  _applyFoodColors() {
    for (const key in this.registry.models) {
      const tint = FOOD_TINTS[key];
      if (!tint) continue;
      const col = new THREE.Color(tint);
      this.registry.models[key].traverse((o) => {
        if (!o.isMesh || !o.material) return;
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        for (const m of mats) {
          m.map = null; m.transparent = false; m.opacity = 1;
          if (m.color) m.color.copy(col);
          m.metalness = 0; m.roughness = 0.75; m.needsUpdate = true;
        }
      });
    }
  }

  // ---------- menu showcase ----------
  _buildShowcase() {
    try {
      this.engine.applyEnvironment(THEMES.dungeon.env);
      this.showcase = new THREE.Group();
      this.engine.scene.add(this.showcase);
      this.menuAnimators = [];

      if (this.registry.has("dungeon/floor")) {
        const cells = [];
        for (let x = -4; x <= 4; x++) for (let z = -4; z <= 3; z++) cells.push([x, z]);
        const inst = instancedTiles(this.registry.instance("dungeon/floor"), cells, TILE);
        if (inst) this.showcase.add(inst);
      }

      // dungeon backdrop built from the real wall models (+ banner decor)
      const placeWall = (x, z, rot) => {
        if (this.registry.has("dungeon/wall")) {
          const w = this.registry.instance("dungeon/wall");
          w.scale.set(0.5, 1.5, 0.5); w.rotation.y = rot; w.position.set(x, 0, z); // taller — fills the backdrop
          w.traverse((o) => { if (o.isMesh) { o.castShadow = false; o.receiveShadow = true; } });
          this.showcase.add(w);
        } else {
          const b = new THREE.Mesh(new THREE.BoxGeometry(TILE, 6, 0.6), new THREE.MeshStandardMaterial({ color: 0x333a48, roughness: 0.9 }));
          b.position.set(x, 3, z); this.showcase.add(b);
        }
      };
      for (let x = -4; x <= 4; x++) placeWall(x * TILE, -4.5 * TILE, 0);
      for (let z = -4; z <= 3; z++) { placeWall(-4.5 * TILE, z * TILE, Math.PI / 2); placeWall(4.5 * TILE, z * TILE, -Math.PI / 2); }
      if (this.registry.has("dungeon/banner")) for (const bx of [-4, -2, 2, 4]) {
        const b = this.registry.instance("dungeon/banner"); b.scale.setScalar(0.85); b.position.set(bx * TILE, 2.7, -4.35 * TILE); this.showcase.add(b);
      }
      if (this.registry.has("dungeon/torch_wall")) {
        for (const tx of [-4.8, 4.8]) {
          const t = this.registry.instance("dungeon/torch_wall");
          t.scale.setScalar(0.8); t.position.set(tx, 3.4, -8.7); this.showcase.add(t);
        }
      }
      for (const [x, z, i] of [[-4.8, -8.4, 2.6], [4.8, -8.4, 2.6], [-6, 2, 1.8], [6, 2, 1.8]]) {
        const L = new THREE.PointLight(0xffb055, i, 22, 1.4);
        L.position.set(x, 3, z); this.showcase.add(L);
      }

      // hero front-centre, facing the camera, sword in hand
      const hero = this._menuChar("characters/knight", 2.0, ["anim/kaykit_general", "anim/kaykit_movement"]);
      if (hero) {
        hero.group.position.set(0, 0, 1.8);
        const hand = hero.model.getObjectByName("handslotr");
        if (hand && this.registry.has("weapon/sword")) hand.add(this.registry.instance("weapon/sword"));
        this.showcase.add(hero.group);
      }
      // skeletons arrayed behind, closing in on the hero
      const foes = [
        ["enemies/skel_warrior", 1.8, -2.9, -1.8, 0.5, "skelw/axe"],
        ["enemies/skel_rogue", 1.7, 2.9, -1.8, -0.5, "skelw/blade"],
        ["enemies/skel_minion", 1.6, 0, -3.6, 0, "skelw/blade"],
      ];
      for (const [key, h, x, z, ry, wkey] of foes) {
        const f = this._menuChar(key, h, ["anim/skel_general", "anim/skel_movement"]);
        if (f) {
          const hand = f.model.getObjectByName("handslotr") || f.model.getObjectByName("handr");
          if (hand && wkey && this.registry.has(wkey)) hand.add(this.registry.instance(wkey));
          f.group.position.set(x, 0, z); f.group.rotation.y = ry; this.showcase.add(f.group);
        }
      }
      this._menuT = 0;
      this._buildAmbiance();
      this._startReveal();
    } catch (_) { /* showcase is cosmetic; never block the menu */ }
  }

  // Soft radial circle texture reused for motes + mist.
  _softCircleTex() {
    if (this._softTex) return this._softTex;
    const c = document.createElement("canvas"); c.width = c.height = 64;
    const g = c.getContext("2d");
    const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.4, "rgba(255,255,255,0.45)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grad; g.fillRect(0, 0, 64, 64);
    this._softTex = new THREE.CanvasTexture(c);
    return this._softTex;
  }

  // Floating purple motes + drifting mist behind the reveal (billboards, not clipped).
  _buildAmbiance() {
    const tex = this._softCircleTex();
    this._motes = [];
    for (let i = 0; i < 46; i++) {
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, color: 0xc77dff, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending, depthWrite: false }));
      const base = 0.12 + Math.random() * 0.34;
      const m = { s, base, x: (Math.random() - 0.5) * 17, z: (Math.random() - 0.5) * 10 - 1, y: Math.random() * 7, speed: 0.25 + Math.random() * 0.55, phase: Math.random() * 6.28, amp: 0.4 + Math.random() * 0.9 };
      s.scale.setScalar(base); s.position.set(m.x, m.y, m.z);
      this.showcase.add(s); this._motes.push(m);
    }
    this._mist = [];
    for (let i = 0; i < 6; i++) {
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, color: 0x7a4fd0, transparent: true, opacity: 0.06, blending: THREE.AdditiveBlending, depthWrite: false }));
      const sc = 8 + Math.random() * 8;
      const m = { s, x: (Math.random() - 0.5) * 15, y: 0.6 + Math.random() * 1.8, z: (Math.random() - 0.5) * 8 - 2, phase: Math.random() * 6.28, drift: 0.12 + Math.random() * 0.2, baseOp: 0.05 + Math.random() * 0.07 };
      s.scale.set(sc, sc * 0.62, 1); s.position.set(m.x, m.y, m.z);
      this.showcase.add(s); this._mist.push(m);
    }
  }

  // Materialize the showcase bottom-up behind a rising golden scan-line
  // (an animated clipping plane) — a "hologram/print" reveal on first paint.
  _startReveal() {
    try {
      this.engine.renderer.localClippingEnabled = true;
      this._revealPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), -1.5); // shows y <= constant
      this._revealMats = [];
      this.showcase.traverse((o) => {
        if (!o.isMesh && !o.isInstancedMesh) return;
        const clip = (mat) => { const m = mat.clone(); m.clippingPlanes = [this._revealPlane]; m.clipShadows = true; this._revealMats.push(m); return m; };
        o.material = Array.isArray(o.material) ? o.material.map(clip) : clip(o.material);
      });
      const glowMat = new THREE.MeshBasicMaterial({ color: 0xc77dff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
      this._revealGlow = new THREE.Mesh(new THREE.PlaneGeometry(30, 30), glowMat);
      this._revealGlow.rotation.x = -Math.PI / 2;
      this.showcase.add(this._revealGlow);
      this._revealT = 0; this._revealDur = 2.3; this._revealing = true;
    } catch (_) { this._revealing = false; }   // no clipping support → just show it (prewarmed)
  }

  _finishReveal() {
    this._revealing = false;
    for (const m of (this._revealMats || [])) m.clippingPlanes = null;
    this._revealMats = [];
    if (this._revealGlow) { this.showcase.remove(this._revealGlow); this._revealGlow.geometry.dispose(); this._revealGlow.material.dispose(); this._revealGlow = null; }
  }

  _menuChar(key, h, libs) {
    if (!this.registry.has(key)) return null;
    const model = this.registry.instance(key);
    fitToHeight(model, h);
    model.traverse((o) => { if (o.isMesh) o.castShadow = true; });
    const group = new THREE.Group();
    group.add(model);
    let clips = model.animations || [];
    for (const k of libs) clips = clips.concat(this.registry.clips(k));
    if (clips.length) { const a = new CharacterAnimator(model, clips); a.setMoving(false); this.menuAnimators.push(a); }
    return { group, model };
  }

  _updateShowcase(dt) {
    for (const a of this.menuAnimators) a.update(dt);
    this._menuT = (this._menuT || 0) + dt;
    const sway = Math.sin(this._menuT * 0.25) * 1.7;
    const cam = this.cameraRig.camera;
    cam.position.set(sway, 2.5, 7.4);   // low, front-on, heroic
    cam.lookAt(0, 1.1, -0.8);

    if (this._revealing) {
      this._revealT += dt;
      const t = Math.min(1, this._revealT / this._revealDur);
      const e = 1 - Math.pow(1 - t, 3);                 // easeOutCubic
      this._revealPlane.constant = -1.5 + e * 8.6;      // sweep reveal height up through the scene
      if (this._revealGlow) {
        this._revealGlow.position.y = this._revealPlane.constant;
        const fadeIn = Math.min(1, t / 0.08);
        this._revealGlow.material.opacity = 0.55 * fadeIn * (1 - t);
      }
      if (t >= 1) this._finishReveal();
    }

    // floating purple motes (rise, drift, twinkle — brighter near the reveal line)
    const T = this._menuT, line = this._revealing ? this._revealPlane.constant : 99;
    if (this._motes) for (const m of this._motes) {
      m.y += m.speed * dt;
      if (m.y > 7.6) { m.y = -0.6; m.phase = Math.random() * 6.28; }
      m.s.position.set(m.x + Math.sin(T * 0.6 + m.phase) * m.amp, m.y, m.z + Math.cos(T * 0.4 + m.phase) * m.amp * 0.5);
      const near = this._revealing ? Math.max(0, 1 - Math.abs(m.y - line) / 2.2) : 0;
      m.s.material.opacity = 0.22 + 0.35 * Math.abs(Math.sin(T * 1.4 + m.phase)) + near * 0.55;
      m.s.scale.setScalar(m.base * (1 + Math.sin(T * 2 + m.phase) * 0.18));
    }
    // drifting mist haze
    if (this._mist) for (const m of this._mist) {
      m.s.position.x = m.x + Math.sin(T * m.drift + m.phase) * 1.7;
      m.s.material.opacity = m.baseOp * (0.7 + 0.3 * Math.sin(T * 0.5 + m.phase)) + (this._revealing ? 0.05 : 0);
    }
  }

  _teardownShowcase() {
    this._revealing = false;
    if (this.showcase) { this.engine.scene.remove(this.showcase); this.showcase = null; }
    this.menuAnimators = [];
  }

  /** Dispose an active/dead run so nothing lingers when returning to menus. */
  _teardownRun() {
    for (const e of this.enemies) e.dispose(); this.enemies = [];
    if (this._hazards) { this._hazards.dispose(); this._hazards = null; }
    this._clearTurrets();
    this._disposeCompanions();
    this.loot.clear();
    this.combat.clearProjectiles();
    if (this.effects && this.effects.clear) this.effects.clear();
    if (this.portal) { this.engine.scene.remove(this.portal.mesh); this.portal = null; }
    for (const n of this.npcs) n.dispose(); this.npcs = [];
    if (this.player) { this.engine.scene.remove(this.player.mesh); this.player = null; }
    if (this.levelBuilder) this.levelBuilder.clear();
    this.inHub = false;
  }

  // ---------- character-select 3D preview (studio-style turntable) ----------
  enterCharSelect(id) { this._teardownRun(); this._teardownShowcase(); this.state = "menu"; this.charSelect = true; this._buildCharPreview(id); }
  setPreviewChar(id) { if (this.charSelect) this._buildCharPreview(id); }
  exitCharSelect() { this.charSelect = false; this._teardownCharPreview(); this._buildShowcase(); }

  _buildCharPreview(id) {
    this._teardownCharPreview();
    const c = getCharacter(id);
    try {
      this.engine.applyEnvironment(THEMES.dungeon.env);
      const grp = new THREE.Group();
      const turn = new THREE.Group(); grp.add(turn);
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.6, 0.2, 40), new THREE.MeshStandardMaterial({ color: 0x241d16, roughness: 0.9 }));
      disc.position.y = -0.1; disc.receiveShadow = true; grp.add(disc);
      const l1 = new THREE.PointLight(0xffcf8a, 13, 16, 2); l1.position.set(-2.5, 3.2, 3); grp.add(l1);
      const l2 = new THREE.PointLight(0xc77dff, 9, 16, 2); l2.position.set(2.5, 2, -2); grp.add(l2);
      this._charAnims = [];
      if (this.registry.has(c.model)) {
        const model = this.registry.instance(c.model);
        fitToHeight(model, 2.0);
        model.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        const wkey = starterKit(c.id).weapon.model;
        const hand = model.getObjectByName("handslotr") || model.getObjectByName("handr");
        if (hand && this.registry.has(wkey)) hand.add(this.registry.instance(wkey));
        let clips = model.animations || [];
        for (const l of ["anim/kaykit_general", "anim/kaykit_movement"]) clips = clips.concat(this.registry.clips(l));
        if (clips.length) { const a = new CharacterAnimator(model, clips); a.setMoving(false); this._charAnims.push(a); }
        turn.add(model);
      }
      this.charPreviewTurn = turn;
      this.charPreview = grp;
      this.engine.scene.add(grp);
    } catch (_) { /* cosmetic */ }
  }

  _updateCharPreview(dt) {
    for (const a of this._charAnims || []) a.update(dt);
    if (this.charPreviewTurn) this.charPreviewTurn.rotation.y += dt * 0.5;
    const cam = this.cameraRig.camera;
    cam.position.set(1.3, 2.3, 6.2);   // look right-of-centre so the hero sits on the left, panel on the right
    cam.lookAt(1.3, 1.05, 0);
  }

  _teardownCharPreview() {
    if (this.charPreview) { this.engine.scene.remove(this.charPreview); this.charPreview = null; }
    this.charPreviewTurn = null; this._charAnims = [];
  }

  // ---------- game flow ----------
  setDifficulty(id) {
    const d = DIFFICULTIES.find((x) => x.id === id);
    if (d) this.difficulty = d;
  }

  /** Pick a hero from the character-select screen, then begin the run. */
  begin(classId) {
    this.character = getCharacter(classId);
    this.profile = this.save.profile(this.character.id);   // load this hero's saved level/XP
    this.start();
  }

  // ---- XP / leveling (persisted per character) ----
  _gainXp(n) {
    if (!this.profile) return;
    this.profile.xp += n;
    let need = this.save.xpNeeded(this.profile.level);
    while (this.profile.xp >= need) {
      this.profile.xp -= need;
      this.profile.level++;
      if (this.player) { this.player.setLevel(this.profile.level); this.player.hp = this.player.maxHp; }
      if (this.player && this.effects) this.effects.text(this.player.pos, `LEVEL ${this.profile.level}!`, "#ffe08a");
      need = this.save.xpNeeded(this.profile.level);
      this._checkAchievements();   // level milestones + newly-unlocked skins
      this._applySkin();
    }
    this.save.markDirty();   // hot path (per kill) → coalesced write
  }

  // ---------- The Helix of Restoration (codex-completion driven) ----------
  // meta.codex = { memId: true } collected memories. Each of the 100 codices is
  // one helix line; a line lights when EVERY memory in its codex is collected.

  /** Record a single memory; returns true if newly collected. */
  _collectMemory(memId) {
    if (!memId) return false;
    if (!this.meta.codex) this.meta.codex = {};
    if (this.meta.codex[memId]) return false;
    this.meta.codex[memId] = true;
    this._awardInsight(2);          // each new memory = a little Insight
    return true;
  }

  /** Forge relics for any codices newly completed; returns [{ codexId, relic }]. */
  _forgeCompletedCodices() {
    const h = this.meta.helix || (this.meta.helix = { forged: {}, restored: false });
    if (!h.forged) h.forged = {};
    const results = [];
    for (let c = 1; c <= MEM_CODEX_COUNT; c++) {
      if (h.forged[c] || !codexComplete(c, this.meta.codex)) continue;
      h.forged[c] = true;
      const relic = makeMemoryRelic(c);          // Legendary Memory Relic for this codex
      if (addItem(this.player.inv, relic, this.player.invCap) === "full") addItem(this._stash(), relic, null);
      results.push({ codexId: c, relic });
    }
    if (completedCodexCount(this.meta.codex) >= MEM_CODEX_COUNT && !h.restored) { h.restored = true; this._onHelixComplete(); }
    this.save.write();
    this._updateHelixMonument();
    return results;
  }

  helixState() {
    const done = completedCodexCount(this.meta.codex || {});
    return {
      sections: done, total: MEM_CODEX_COUNT, restored: !!(this.meta.helix && this.meta.helix.restored),
      collected: totalCollected(this.meta.codex || {}), library: TOTAL_MEMORIES,
    };
  }

  /** Turn in EVERY memory fragment in the bag (stack-aware). Records codices + forges relics. */
  turnInMemories() {
    const p = this.player;
    let count = 0, fresh = 0;
    for (let i = 0; i < p.inv.length; i++) {
      const it = p.inv[i];
      if (!it || it.kind !== "fragment") continue;
      if (this._collectMemory(it.fragId)) fresh++;   // duplicates of a memory add nothing new
      count += stackCount(it);
      p.inv[i] = null;                                // leaves the slot empty (gap kept)
    }
    if (!count) return { count: 0, fresh: 0, relics: [], codices: [], helix: this.helixState() };
    const forged = this._forgeCompletedCodices();
    return { count, fresh, relics: forged.map((f) => f.relic), codices: forged.map((f) => f.codexId), helix: this.helixState() };
  }

  /** Fill all 100 codices → the world is restored. */
  _onHelixComplete() {
    this.save.addNotoriety(500);
    if (this._barrier) {   // the protective barrier blooms gold — the world mends
      this._barrier.mat.color.set(0xffe8a3);
      this._barrier.cap.material.color.set(0xffe8a3);
    }
    if (this.player) this.effects.text(this.player.pos, "THE HELIX IS WHOLE — THE WORLD IS RESTORED", "#ffe8a3");
  }

  // ---------- Research / Insight ----------
  /** Sum every owned perk node's `grants` into one modifier object. */
  researchMods() {
    const m = { dmg: 0, hp: 0, stam: 0, mana: 0, crit: 0, speed: 0, insight: 0, luck: 0 };
    const owned = this.meta.research || {};
    for (const n of RESEARCH) {
      if (!owned[n.id] || !n.grants) continue;
      for (const k in n.grants) m[k] = (m[k] || 0) + n.grants[k];
    }
    return m;
  }
  /** Push current research modifiers onto the player and rescale their stats. */
  _applyResearchToPlayer() {
    if (!this.player) return;
    const mods = this.researchMods();
    // fold in the active pet's passive grants (warden/channeler/duelist/forager)
    if (this._activePet) { const g = this._activePet.petMods(); for (const k in g) mods[k] = (mods[k] || 0) + g[k]; }
    this.player.rmods = mods;
    this.player.attuneAny = this.hasResearch("attune");
    this.player.applyGear();
    this._syncBagCap();   // persistent bag-slot expansions
    this._applySkin();    // recolor skin (level-gated / mastery)
    if (this.loot) this.loot.luck = 1 + (mods.luck || 0);
  }

  // ---------- Run persistence (pause / save & exit / continue / autosave) ----------
  /** Snapshot the current run so it can be resumed exactly (hero + loadout + floor). */
  _serializeRun() {
    const p = this.player; if (!p || !this.character) return null;
    const run = {
      v: RUN_VERSION,
      hero: this.character.id,
      floor: this.floor,
      difficulty: this.difficulty ? this.difficulty.id : "veteran",
      themeId: this.theme ? this.theme.id : null,
      inHub: !!this.inHub,
      totalKills: this.totalKills || 0,
      wispDismissed: !!this.wispDismissed,
      gold: p.gold || 0,
      keys: p.keys || 0,
      hp: Math.round(p.hp),
      mana: Math.round(p.mana || 0),
      weapon: p.weapon, armor: p.armor, inv: p.inv, foods: p.foods,
      savedAt: Date.now(),
    };
    // dungeon floors also persist the exact tactical state (seed → identical map)
    if (!this.inHub && this.level) {
      run.seed = this._floorSeed;
      run.player = { x: p.pos.x, z: p.pos.z, yaw: p.yaw, groundH: p.groundH, stamina: Math.round(p.stamina || 0) };
      run.enemies = (this.enemies || []).map((e) => ({ dead: !!e.dead, hp: Math.round(e.hp), x: e.pos.x, z: e.pos.z, forgotten: !!e.forgotten, shade: e.shadeName || null }));
      run.loot = (this.loot.pickups || []).map((pk) => ({ item: pk.item, x: pk.x, z: pk.z }));
      run.portalOpen = !!this.portal;
      run.fog = this.minimap.serialize ? this.minimap.serialize() : null;
      // which barrels/crates/chests were already smashed (index-aligned via seed)
      run.brokenDest = [];
      (this.levelBuilder.destructibles || []).forEach((d, i) => { if (d.broken) run.brokenDest.push(i); });
      // unlocked chests keep their REMAINING contents (keys spent stay spent, and
      // any loot you left behind is still there on resume)
      run.chests = [];
      (this.levelBuilder.lockedChests || []).forEach((c, i) => {
        if (c.unlocked) run.chests.push({ i, contents: (c.contents || []).map((s) => s || null) });
      });
      run.openDoors = [];
      (this.levelBuilder.doors || []).forEach((d, i) => { if (d.open) run.openDoors.push(i); });
    }
    return run;
  }
  _autosave() { const r = this._serializeRun(); if (r) this.save.saveRun(r); }

  /** Push a restored loadout onto the freshly-built player. */
  _applyPendingLoadout() {
    const L = this._pendingLoadout; if (!L || !this.player) return;
    const p = this.player;
    if (L.weapon) p.weapon = rehydrateItem(L.weapon);
    if (L.armor) p.armor = rehydrateItem(L.armor);
    if (Array.isArray(L.inv)) p.inv = L.inv.map((i) => (i ? rehydrateItem(i) : null));
    if (Array.isArray(L.foods)) p.foods = L.foods.map((f) => (f ? rehydrateItem(f) : null));
    p.gold = L.gold || 0;
    p.keys = L.keys || 0;
    p.applyGear();
    p.refreshPlaceholderColors && p.refreshPlaceholderColors();
    if (L.hp != null) p.hp = Math.max(1, Math.min(p.maxHp, L.hp));
    if (L.mana != null) p.mana = Math.max(0, Math.min(p.maxMana, L.mana));
    this._pendingLoadout = null;
  }

  /**
   * Normalize a loaded run snapshot to the current shape. Older/partial runs are
   * repaired to safe defaults rather than wiping meta progress. CONTRACT: any new
   * field added to `_serializeRun` must have a default here (or a defensive read
   * at the use site) so a run written by an older build resumes cleanly.
   */
  _normalizeRun(run) {
    if (!run || typeof run !== "object") return null;
    run.v = run.v || 0;
    run.keys = run.keys || 0;
    run.totalKills = run.totalKills || 0;
    if (!Array.isArray(run.inv)) run.inv = [];
    if (!Array.isArray(run.foods)) run.foods = [null, null, null];
    if (!Array.isArray(run.chests)) run.chests = [];
    if (!Array.isArray(run.openDoors)) run.openDoors = [];
    if (!Array.isArray(run.brokenDest)) run.brokenDest = [];
    if (!Array.isArray(run.enemies)) run.enemies = [];
    if (!Array.isArray(run.loot)) run.loot = [];
    run.v = RUN_VERSION;   // stamp forward so a re-save carries the current version
    return run;
  }

  /** Resume the saved run from the main menu. */
  continueRun() {
    const run = this._normalizeRun(this.save.getRun()); if (!run) return;
    this.character = getCharacter(run.hero);
    this.profile = this.save.profile(this.character.id);
    const d = DIFFICULTIES.find((x) => x.id === run.difficulty); if (d) this.difficulty = d;
    this.floor = run.floor || 1;
    this.totalKills = run.totalKills || 0;
    this.wispDismissed = !!run.wispDismissed;
    this._prevThemeId = null;
    this._pendingLoadout = {
      gold: run.gold, keys: run.keys || 0, hp: run.hp, mana: run.mana,
      weapon: run.weapon, armor: run.armor, inv: run.inv || [], foods: run.foods || [null, null, null],
    };
    this.charSelect = false; this._teardownCharPreview(); this._teardownShowcase();
    if (this.player) { this.engine.scene.remove(this.player.mesh); this.player = null; }
    this.inHub = false;
    if (run.inHub) { this._enterHub(); }               // resume in the Waystation
    else {
      // regenerate the exact floor (seed) and re-apply the saved tactical state
      this._resumeThemeId = run.themeId;
      this._resumeSeed = run.seed;
      this._resumeState = { player: run.player, enemies: run.enemies, loot: run.loot, portalOpen: run.portalOpen, fog: run.fog };
      this._newFloor(true);
      this.audio.play(this._boss ? "boss" : "game");
    }
    this.hud.hideScreens();
    this.state = "playing";
    this._showRecap();
  }

  /** Build + show the story recap for the current hero, freezing the sim until
   *  the player descends. Records a snapshot so the next recap can diff against it. */
  _showRecap() {
    if (!this.profile || !this.character || !this.hud) return;
    const seed = (((this.meta.recapCount || 0) + 1) * 2654435761) ^ (this.profile.kills || 0) ^ ((this.profile.best || 0) << 8);
    let data;
    try { data = buildRecap({ character: this.character, profile: this.profile, meta: this.meta, prevSnap: this.profile._recapSnap, seed }); }
    catch (e) { console.warn("[Relikara] recap build failed", e); return; }
    this.meta.recapCount = (this.meta.recapCount || 0) + 1;
    this.profile._recapSnap = data.snapshot;
    this.save.write();
    this._recapOpen = true;
    this.hud.showRecap(data, () => { this._recapOpen = false; });
  }

  /** [Esc] — pause / resume. */
  togglePause() {
    if (this.state === "playing") {
      this.state = "paused";
      if (this.audio.current) this.audio.current.pause();
      this.hud.showPause();
    } else if (this.state === "paused") {
      this.state = "playing";
      if (this.audio.current) this.audio.current.play().catch(() => {});
      this.hud.hidePause();
    }
  }

  /** Save the run and return to the main menu (reload for a clean state). */
  saveAndExit() { this._autosave(); location.reload(); }

  // ---------- Pets ----------
  // Two independent slots: the utility WISP (loot magnet, toggled with [P]) and
  // a CREATURE companion (chosen in the Menagerie). Both can be active at once
  // and orbit on opposite sides of the hero.
  _spawnCompanions() { this._spawnWisp(); this._spawnActivePet(); }

  _updateCompanions(dt) {
    if (!FEATURES.familiar) return;
    const ctx = { player: this.player, loot: this.loot, enemies: this.enemies, combat: this.combat, onCollect: (it) => this._onFamiliarPickup(it) };
    if (this._wisp) this._wisp.update(dt, ctx);
    if (this._activePet) this._activePet.update(dt, ctx);
  }
  _disposeCompanions() {
    if (this._wisp) { this._wisp.dispose(); this._wisp = null; }
    if (this._activePet) { this._activePet.dispose(); this._activePet = null; }
  }
  _awardPetXp(n) {
    const grant = (petId, inst, affectsMods) => {
      if (!inst) return;
      const up = this.save.addPetXp(petId, n);
      if (up) { inst.setLevel(up); if (affectsMods) this._applyResearchToPlayer(); if (this.effects) this.effects.text(this.player.pos, `${getPet(petId).name} → Lv ${up}`, "#7bd645"); }
    };
    grant("wisp", this._wisp, false);
    if (this.meta.activePet) grant(this.meta.activePet, this._activePet, true);
  }

  _spawnWisp() {
    if (this._wisp) { this._wisp.dispose(); this._wisp = null; }
    if (!FEATURES.familiar || this.wispDismissed) return;
    const st = this.save.petState("wisp");
    if (!st.unlocked) return;
    this._wisp = new Pet(this.engine.scene, this.registry, getPet("wisp"), st.level);
    this._wisp.phase = Math.PI;   // opposite side from the creature companion
    if (this.player) this._wisp.snapTo(this.player.pos);
  }

  _spawnActivePet() {
    if (this._activePet) { this._activePet.dispose(); this._activePet = null; }
    if (!FEATURES.familiar) return;
    const id = this.meta.activePet;
    if (!id || id === "wisp") return;             // creature slot only
    const st = this.save.petState(id);
    if (!st.unlocked) return;
    this._activePet = new Pet(this.engine.scene, this.registry, getPet(id), st.level);
    if (this.player) this._activePet.snapTo(this.player.pos);
  }

  /** [P] — summon or dismiss the utility Wisp. */
  toggleWisp() {
    if (!this.save.petState("wisp").unlocked) {
      if (this.effects && this.player) this.effects.text(this.player.pos, "No Wisp tamed yet", "#b7a9d0");
      return;
    }
    this.wispDismissed = !this.wispDismissed;
    if (this.wispDismissed) { if (this._wisp) { this._wisp.dispose(); this._wisp = null; } if (this.effects) this.effects.text(this.player.pos, "Wisp dismissed", "#b7a9d0"); }
    else { this._spawnWisp(); if (this.effects) this.effects.text(this.player.pos, "Wisp summoned", "#7bd645"); }
  }

  /** Choose the active creature companion (from the Menagerie). */
  setActiveCompanion(id) {
    if (id && !this.save.petState(id).unlocked) return false;
    this.meta.activePet = id || null; this.save.write();
    this._spawnActivePet(); this._applyResearchToPlayer();
    if (id && this.effects && this.player) this.effects.text(this.player.pos, `${getPet(id).name} joins you`, "#7bd645");
    return true;
  }
  /** Switch the active companion (from the Menagerie). */
  setActivePet(id) {
    if (!this.save.setActivePet(id)) return false;
    this._spawnActivePet(); this._applyResearchToPlayer();
    if (this.effects && this.player) this.effects.text(this.player.pos, `${getPet(id).name} joins you`, "#7bd645");
    return true;
  }
  _checkPetUnlocks() {
    for (const p of PETS) {
      if (p.unlockDepth > 0 && (this.meta.deepest || 0) >= p.unlockDepth && !this.save.petState(p.id).unlocked) {
        this.save.unlockPet(p.id);
        if (p.utility) {   // the Wisp — auto-summon and remind of the hotkey
          if (this.effects && this.player) this.effects.text(this.player.pos, `${p.name} tamed! [P] to summon/dismiss`, "#ffcf6f");
          this.wispDismissed = false; this._spawnWisp();
        } else {           // a creature companion — unlocked only; the player chooses
          // whether to summon it (and which) at the Menagerie. Never auto-equip.
          if (this.effects && this.player) this.effects.text(this.player.pos, `Companion tamed: ${p.name}! Summon it at the Menagerie.`, "#ffcf6f");
        }
      }
    }
  }
  _awardInsight(n) {
    const mult = 1 + (this.researchMods().insight || 0);
    this.save.addInsight(Math.round(n * mult));
  }
  luckMult() { return 1 + (this.researchMods().luck || 0); }
  _noteDepth() {
    if (this.profile && this.floor > (this.profile.best || 0)) this.profile.best = this.floor;
    if (this.floor > (this.meta.deepest || 0)) { this.meta.deepest = this.floor; this.save.write(); this._checkPetUnlocks(); }
    this._checkAchievements();
  }

  // ---------- Achievements (per-character) + unlockable skins ----------
  _checkAchievements() {
    const prof = this.profile; if (!prof) return;
    const cid = this.character && this.character.id; if (!cid) return;
    for (const a of achievementsFor(cid)) {
      if (prof.achievements && prof.achievements[a.id]) continue;
      if (achieveProgress(prof, a).done && this.save.unlockAchievement(cid, a.id)) {
        if (this.effects && this.player) this.effects.text(this.player.pos, `★ Achievement — ${a.title}`, "#ffe08a");
        this._applySkin();   // a newly-earned feat may unlock a skin
      }
    }
    if (allEarned(prof, cid) && !prof.mastered) {
      prof.mastered = true; this.save.write();
      if (this.effects && this.player) this.effects.text(this.player.pos, `✦ ${this.character.name} MASTERED — Vaultgold skin unlocked!`, "#f0c34b");
    }
  }

  /** Apply the hero's chosen (or highest-unlocked) recolor skin to the model. */
  _applySkin() {
    if (!this.player || !this.player.mesh) return;
    const avail = unlockedSkins(this.profile || {}, this.character && this.character.id);
    let skin = skinById((this.profile && this.profile.skin) || "default");
    if (!avail.some((s) => s.id === skin.id)) skin = SKINS[0];   // fall back if not yet unlocked
    const col = skin.tint;
    this.player.mesh.traverse((o) => {
      if (o.isMesh && o.material) {
        const ms = Array.isArray(o.material) ? o.material : [o.material];
        for (const m of ms) if (m.color) m.color.set(col == null ? 0xffffff : col);
      }
    });
  }
  /** Set the active skin for the current hero (called from the UI). */
  setSkin(id) {
    if (!this.profile) return false;
    if (!unlockedSkins(this.profile, this.character && this.character.id).some((s) => s.id === id)) return false;
    this.profile.skin = id; this.save.write(); this._applySkin(); return true;
  }
  hasResearch(id) { return !!(this.meta.research && this.meta.research[id]); }

  // derived crafting gates read by the Waystation
  smithTier() { return this.hasResearch("forge_4") ? 4 : this.hasResearch("forge_3") ? 3 : this.hasResearch("forge_2") ? 2 : this.hasResearch("forge_1") ? 1 : 0; }
  forgeCountCap() { return [0, 3, 6, 10, 14][this.smithTier()]; }
  forgeRarityCapIdx() { return [0, 1, 2, 3, 3][this.smithTier()]; }
  canReinforce() { return this.hasResearch("armorsmith"); }
  arcanaTier() { return this.hasResearch("ench_3") ? 3 : this.hasResearch("ench_2") ? 2 : this.hasResearch("ench_1") ? 1 : 0; }
  canEnchant() { return this.arcanaTier() >= 1; }
  enchantPowerCap() { return [0, 1.5, 2.6, 3.4][this.arcanaTier()]; }
  attuneAny() { return this.hasResearch("attune"); }
  canBrew() { return this.hasResearch("surv_1"); }
  brewGreater() { return this.hasResearch("surv_2"); }

  /** owned | available | poor (can't afford) | locked (prereq/depth). */
  researchStatus(node) {
    const m = this.meta;
    if (m.research[node.id]) return "owned";
    const prereqOk = (node.requires || []).every((r) => m.research[r]);
    const depthOk = (m.deepest || 0) >= node.reqDepth;
    if (!prereqOk || !depthOk) return "locked";
    return (m.insight || 0) >= node.cost ? "available" : "poor";
  }
  tryResearch(id) {
    const node = getResearch(id); if (!node) return { ok: false, msg: "Unknown research." };
    const st = this.researchStatus(node);
    if (st === "owned") return { ok: false, msg: "Already researched." };
    if (st === "locked") return { ok: false, msg: `Locked — reach depth ${node.reqDepth} and its prerequisites first.` };
    if (st === "poor") return { ok: false, msg: `Need ${node.cost} Insight.` };
    if (this.save.unlockResearch(id, node.cost)) {
      this._applyResearchToPlayer();   // apply new perks/attunement immediately
      return { ok: true, msg: `Researched ${node.name}.` };
    }
    return { ok: false, msg: "Could not research." };
  }

  debugClearSave() {
    this.save.clear();
    this.meta = this.save.meta();   // reset Vault notoriety / shades / codex too
    if (this.character) this.profile = this.save.profile(this.character.id);
  }

  start() {
    this.charSelect = false; this._teardownCharPreview();
    this._teardownShowcase();
    // A new run resets only RUN-SCOPED state — you begin the descent weak again:
    // starter kit, empty bag, no gold, floor 1, freshly-seeded map. META persists
    // (the Helix + collected memories, research, stash, pets, notoriety, and your
    // hero profile) — that's the backdrop the fresh climb builds toward. A full
    // wipe is an intentional opt-in only (Settings → New Legacy).
    this.save.clearRun();            // a new run replaces any saved one
    this._pendingLoadout = null;
    this.wispDismissed = false;
    this.floor = 1;
    this.totalKills = 0;
    this._prevThemeId = null;
    if (this.player) { this.engine.scene.remove(this.player.mesh); this.player = null; }
    this._newFloor(true);
    this.hud.hideScreens();
    this.audio.play(this._boss ? "boss" : "game");
    this.state = "playing";
    this._autosave();
    this._showRecap();
  }

  _newFloor(fresh) {
    for (const e of this.enemies) e.dispose();
    this.enemies = [];
    this.loot.clear();
    this.combat.clearProjectiles();
    if (this._hazards) { this._hazards.dispose(); this._hazards = null; }
    this._clearTurrets();
    if (this.portal) { this.engine.scene.remove(this.portal.mesh); this.portal = null; }

    const resume = this._resumeState;   // set by continueRun for a mid-floor resume

    // pick the theme OUTSIDE the seeded block (saved explicitly, so the seeded
    // random stream lines up at generation time on both first-play and resume)
    const isBossFloor = !!this._bossFloors()[this.floor];
    if (isBossFloor) { this.theme = THEMES.dungeon; this._resumeThemeId = null; }   // bosses always fight in a fresh dungeon arena
    else if (resume && this._resumeThemeId && THEMES[this._resumeThemeId]) { this.theme = THEMES[this._resumeThemeId]; this._resumeThemeId = null; }
    else { this.theme = (this.debugBiome && THEMES[this.debugBiome]) ? THEMES[this.debugBiome] : pickTheme(this.floor, this._prevThemeId); this.debugBiome = null; }
    this._prevThemeId = this.theme.id;

    // seed the deterministic build so a resume regenerates the identical map + enemies
    const seed = (this._resumeSeed != null) ? (this._resumeSeed >>> 0) : ((Math.random() * 4294967296) >>> 0);
    this._floorSeed = seed; this._resumeSeed = null;
    const _rand = Math.random;
    Math.random = makeRng(seed);
    try {
      this.level = isBossFloor ? generateArena(this.floor, this.theme) : this.theme.generator(this.floor, this.theme);
      this.levelBuilder.build(this.level, this.theme);
      // environmental traps — normal dungeon floors only (seeded → resume-stable)
      if (GAME.dungeonHazards && !isBossFloor && this.theme.id === "dungeon") {
        this._hazards = new Hazards(this.levelBuilder.group, this.registry, this.level, this.floor);
      }
      if (fresh || !this.player) this.player = new Player(this.engine.scene, this.registry, this.level, this.character.model, this.character.id, this.profile ? this.profile.level : 1);
      else this.player.spawnAt(this.level);
      this._applyPendingLoadout();       // restore saved gear/inventory when resuming
      this.enemies = isBossFloor ? [] : spawnEnemies(this.engine.scene, this.registry, this.level, this.floor, this.player, this.theme, GAME.enemyBaseCount, GAME.enemyPerFloor, this.difficulty);
      this._maybeSpawnBoss();
      if (!resume && !isBossFloor) { this._maybeSummonForgotten(); this._maybeImpossibleRoom(); this._scatterFloorKeys(); }
    } finally { Math.random = _rand; }

    this.engine.applyEnvironment(this.theme.env);
    this._noteDepth();
    this._spawnCompanions();
    this._applyResearchToPlayer();
    this.combat.reset(this.enemies, this.player, this.level);
    this.combat.destructibles = this.levelBuilder.destructibles;
    this.minimap.reset(this.level);

    // Boss floors always restart the encounter fresh (never restore a mid-fight
    // position/enemy state — that would drop you outside the regenerated arena).
    if (resume && !isBossFloor) { this._applyResumeState(resume); }
    this._resumeState = null;

    this._pcx = this._pcy = null;
    this.level.buildFlow(this.player.pos.x, this.player.pos.z);
    this.cameraRig.snapTo(this.player.pos.x, this.player.pos.z, this.player.groundH || 0);
  }

  /** Re-apply the live tactical state (player position, enemy hp/deaths, ground
   *  loot, portal, fog) onto the freshly-regenerated floor. */
  _applyResumeState(r) {
    const p = this.player;
    if (r.player && p) {
      const gy = this.level.groundY ? this.level.groundY(r.player.x, r.player.z) : (r.player.groundH || 0);
      p.groundH = gy;
      p.pos.set(r.player.x, gy, r.player.z);
      p.mesh.position.copy(p.pos);
      p.yaw = p.desiredYaw = r.player.yaw || 0;
      if (r.player.stamina != null) p.stamina = Math.min(p.maxStamina, r.player.stamina);
    }
    if (Array.isArray(r.enemies)) {
      for (let i = 0; i < this.enemies.length && i < r.enemies.length; i++) {
        const e = this.enemies[i], s = r.enemies[i];
        if (s.dead) { e.dead = true; e.mesh.visible = false; continue; }
        if (s.forgotten) this._applyForgottenBuff(e, s.shade);
        e.hp = Math.min(e.maxHp, s.hp);
        e.pos.x = s.x; e.pos.z = s.z;
        e.groundH = this.level.groundY ? this.level.groundY(s.x, s.z) : (e.groundH || 0);
        e.mesh.position.set(s.x, e.groundH, s.z);
      }
    }
    // remove already-smashed barrels/crates/chests so they don't respawn intact
    if (Array.isArray(r.brokenDest)) {
      const dl = this.levelBuilder.destructibles || [];
      for (const i of r.brokenDest) {
        const d = dl[i]; if (!d) continue;
        d.broken = true;
        if (d.obj && d.obj.parent) d.obj.parent.remove(d.obj);
        if (this.level.block[d.gy]) this.level.block[d.gy][d.gx] = 0;
        this.level.clearBlockerAt(d.x, d.z);
      }
    }
    // restore unlocked chests (lid swung, lock gone, remaining contents intact)
    const _restoreChest = (c) => {
      c.unlocked = true; c.opening = false; c.openT = 1;
      if (c.lid) c.lid.rotation.x = -2.0;
      if (c.lock && c.lock.parent) { c.lock.parent.remove(c.lock); c.lock = null; }
    };
    if (Array.isArray(r.chests)) {
      const lc = this.levelBuilder.lockedChests || [];
      for (const rec of r.chests) {
        const c = lc[rec.i]; if (!c) continue;
        _restoreChest(c);
        c.contents = (rec.contents || []).map((s) => (s ? rehydrateItem(s) : null));
      }
    } else if (Array.isArray(r.openedChests)) {   // legacy saves: mark as looted-empty
      const lc = this.levelBuilder.lockedChests || [];
      for (const i of r.openedChests) { const c = lc[i]; if (!c) continue; _restoreChest(c); c.contents = [null, null, null, null, null]; }
    }
    // restore unlocked doors (open, cells walkable, mesh raised)
    if (Array.isArray(r.openDoors)) {
      const dl = this.levelBuilder.doors || [];
      for (const i of r.openDoors) {
        const d = dl[i]; if (!d) continue;
        d.open = true; d.opening = false;
        for (const [cx, cy] of d.cells) if (this.level.block[cy]) this.level.block[cy][cx] = 0;
        if (d.mesh) d.mesh.position.y = d.openY;
      }
      this.level.buildFlow(this.player.pos.x, this.player.pos.z);
    }
    if (Array.isArray(r.loot)) for (const l of r.loot) { if (l.item) this.loot.dropItem(rehydrateItem(l.item), l.x, l.z, 0); }
    if (r.portalOpen && !this.portal) { this.portal = createPortal(this.levelBuilder.portalPos); this.engine.scene.add(this.portal.mesh); }
    if (r.fog && this.minimap.restore) this.minimap.restore(r.fog);
  }

  _onEnemyKilled(e) {
    this.totalKills++;
    // a slain mimic disgorges the hoard it was hiding (the key you spent pays off)
    if (e.mimicTier) this._spawnLootBurst(rollChest(e.mimicTier, this.floor), e.pos.x, e.pos.z);
    if (this.player) this.player.mana = Math.min(this.player.maxMana, this.player.mana + GAME.manaPerKill);  // essence from the slain
    if (this.profile) { this.profile.kills++; if (e.boss) this.profile.bosses = (this.profile.bosses || 0) + 1; }
    this._gainXp(4 + this.floor * 2);
    this._checkAchievements();

    // The weapon remembers the kill — and may awaken a tier.
    if (this.player) {
      const bonus = e.forgotten ? 5 : 1;             // Forgotten count for more
      const advanced = recordKill(this.player.weapon, bonus, this.player.classId);
      if (advanced) this._relicWhisper(this.player.weapon, advanced);
      else if (this.player.weapon.named && Math.random() < 0.06) this._relicWhisper(this.player.weapon, null);
    }
    // The Vault takes note.
    this.meta.notoriety = Math.max(0, (this.meta.notoriety || 0) + (e.forgotten ? 3 : 0.5));
    if (e.forgotten) this._awardInsight(5);   // studying a Forgotten yields Insight

    this._awardPetXp(e.forgotten ? 8 : 3);   // both active companions gain XP

    this.loot.maybeDrop(e.pos.x, e.pos.z, this.floor, e.groundH || 0);
    // rare relic / lore drops, richer the deeper + more notorious you are
    const luck = this.luckMult();
    const nr = rollNamedRelic(this.floor, this.meta.notoriety * luck) || (e.forgotten && Math.random() < 0.5 ? rollNamedRelic(99, 9999) : null);
    if (nr) { this.loot.dropItem(nr, e.pos.x, e.pos.z, e.groundH || 0); this.effects.text(e.pos, "A relic stirs…", "#c77dff"); }
    if (e.forgotten || Math.random() < (0.02 + this.floor * 0.004) * luck) this.loot.dropItem(makeMemoryItem(rollMemory(this.meta.codex, this.meta.deepest)), e.pos.x, e.pos.z, e.groundH || 0);

    // The Necromancer raises the fallen while it still lives.
    if (this._boss && !this._boss.dead && this._boss.type.reanimator && e !== this._boss && !e.boss) {
      if ((this._reanimated || 0) < 30) {
        this._reanimated = (this._reanimated || 0) + 1;
        const at = { x: e.pos.x, z: e.pos.z };
        setTimeout(() => this._reanimate(at), 1500);
      }
    }

    e.mesh.visible = false;
    setTimeout(() => e.dispose(), 300);
    if (this.enemies.every((en) => en.dead) && !this.portal) this._openPortal();
  }

  // ---------- Bosses (floor 4: Golem +5 adds · floor 7: Necromancer +10) ----------
  _bossFloors() {
    return {
      4: { key: "SkeletonGolem", adds: 10, addKeys: ["SkeletonWarrior", "SkeletonRogue", "SkeletonMinion"], name: "THE SKELETON GOLEM" },
      7: { key: "Necromancer", adds: 20, addKeys: ["SkeletonMinion", "SkeletonWarrior", "SkeletonRogue", "SkeletonArcher"], name: "THE NECROMANCER" },
    };
  }
  _maybeSpawnBoss() {
    const b = this._bossFloors()[this.floor];
    this._boss = null; this._reanimated = 0;
    if (!b || this.inHub) return;
    const spot = this._pickBossSpot();
    const mk = (key, x, z) => {
      const e = new Enemy(this.engine.scene, this.registry, ENEMY_DEFS[key], x, z, this.floor, this.theme.enemyKey, this.difficulty.statMult);
      e.groundH = this.level.groundY ? this.level.groundY(x, z) : 0; e.pos.y = e.groundH; e.mesh.position.y = e.groundH;
      this.enemies.push(e); return e;
    };
    this._boss = mk(b.key, spot.x, spot.z);
    for (let i = 0; i < b.adds; i++) {
      const s = this._scatterSpot(spot, 5);
      mk(b.addKeys[i % b.addKeys.length], s.x, s.z);
    }
    if (this.effects && this.player) this.effects.text(this.player.pos, `${b.name} AWAKENS`, "#ff8a5a");
  }
  _pickBossSpot() {
    // arenas designate a north-centre boss spot
    if (this.level.bossSpot) return this.level.gridToWorld(this.level.bossSpot.gx + 0.5, this.level.bossSpot.gy + 0.5);
    const pts = this.level.spawnPoints || [];
    const px = this.player.pos.x, pz = this.player.pos.z;
    let best = null, bd = -1;
    for (const sp of pts) {
      const w = this.level.gridToWorld(sp.gx + 0.5, sp.gy + 0.5);
      const d = Math.hypot(w.x - px, w.z - pz);
      if (d > bd && !this.level.collidesWorld(w.x, w.z, 1.3)) { bd = d; best = w; }
    }
    if (best) return best;
    const ex = this.level.exit || { gx: 10, gy: 10 };
    return this.level.gridToWorld(ex.gx + 0.5, ex.gy + 0.5);
  }
  _scatterSpot(center, r) {
    for (let t = 0; t < 10; t++) {
      const a = Math.random() * 6.283, rr = 1.6 + Math.random() * r;
      const x = center.x + Math.cos(a) * rr, z = center.z + Math.sin(a) * rr;
      if (!this.level.collidesWorld(x, z, 0.55)) return { x, z };
    }
    return { x: center.x + 1.6, z: center.z };
  }
  /** Necromancer reanimates a fallen skeleton at `at` (only while the boss lives). */
  _reanimate(at) {
    if (!this._boss || this._boss.dead || this.state !== "playing" || this.inHub) return;
    const s = this._scatterSpot(at, 0.6);
    const e = new Enemy(this.engine.scene, this.registry, ENEMY_DEFS.SkeletonMinion, s.x, s.z, this.floor, this.theme.enemyKey, this.difficulty.statMult);
    e.groundH = this.level.groundY ? this.level.groundY(s.x, s.z) : 0; e.pos.y = e.groundH; e.mesh.position.y = e.groundH;
    e.reanimated = true;
    this.enemies.push(e);                 // combat.enemies references the same array
    if (this.effects) { this.effects.text(at, "REANIMATED", "#9a6aff"); this.effects.burst({ x: at.x, y: 1, z: at.z }, "#9a6aff", 12); }
  }

  /**
   * "Some rooms have never existed until someone opens the door." A chance each
   * floor that the Vault shifts and reveals an unforeseen hoard somewhere on the
   * level — richer the deeper you are.
   */
  _maybeImpossibleRoom() {
    if (this.inHub) return;
    if (Math.random() > 0.26) return;
    const pts = this.level.spawnPoints || [];
    if (!pts.length) return;
    const sp = pts[(Math.random() * pts.length) | 0];
    const w = this.level.gridToWorld(sp.gx, sp.gy);
    const y = this.level.groundY ? this.level.groundY(w.x, w.z) : 0;
    const n = 2 + ((Math.random() * 2) | 0);
    for (let i = 0; i < n; i++) {
      const it = rollLoot("chest", this.floor + 2);
      if (it) this.loot.dropItem(it, w.x + (Math.random() * 2 - 1), w.z + (Math.random() * 2 - 1), y);
    }
    if (this.player && this.effects) this.effects.text(this.player.pos, "The Vault shifts — a room now exists that did not before.", "#c77dff");
  }

  /**
   * The Forgotten: fallen heroes the Vault sends back. With a chance that rises
   * with depth + notoriety, promote one live enemy into a shade of a recorded
   * dead hero — larger, tougher, aura-lit, and worth far more.
   */
  _maybeSummonForgotten() {
    const shades = this.meta.shades || [];
    if (!shades.length || this.floor < 2 || this.inHub) return;
    const chance = Math.min(0.6, 0.12 + this.floor * 0.05 + (this.meta.notoriety || 0) * 0.0008);
    if (Math.random() > chance) return;
    const alive = this.enemies.filter((e) => !e.dead);
    if (!alive.length) return;
    const e = alive[(Math.random() * alive.length) | 0];
    const shade = shades[(Math.random() * shades.length) | 0];
    this._applyForgottenBuff(e, shade.name);
    if (this.effects) this.effects.text(e.pos, `THE FORGOTTEN · ${e.shadeName}`, "#c77dff");
  }

  /** Turn a live enemy into a Forgotten shade (stats + aura). Reused on resume. */
  _applyForgottenBuff(e, shadeName) {
    e.forgotten = true;
    e.shadeName = shadeName || "A Forgotten Hero";
    e.maxHp = Math.round(e.maxHp * 2.4); e.hp = e.maxHp;
    e.dmg = Math.round(e.dmg * 1.7);
    e.mesh.scale.multiplyScalar(1.35);
    const aura = new THREE.PointLight(0xc77dff, 2.2, 8, 1.6); aura.position.y = 1.4; e.mesh.add(aura);
  }

  /** Show a relic's whisper / awakening line as floating text. */
  _relicWhisper(item, tier) {
    if (!item) return;
    let line = null, color = "#c9a7ff";
    if (tier) { line = tier.line || `${item.name} awakens: ${tier.name}`; color = tier.color || color; }
    else if (item.whispers && item.whispers.length) line = "“" + item.whispers[Math.floor(Math.random() * item.whispers.length)] + "”";
    if (line && this.player && this.effects) this.effects.text(this.player.pos, line, color);
  }

  _onBreak(d) {
    if (d.obj && d.obj.parent) d.obj.parent.remove(d.obj);
    if (this.level.block[d.gy]) this.level.block[d.gy][d.gx] = 0;
    this.level.clearBlockerAt(d.x, d.z);   // walkable where the barrel/crate stood
    const ey = this.level.elevY[d.gy] ? this.level.elevY[d.gy][d.gx] : 0;
    const item = rollLoot(d.table, this.floor);
    if (item) this.loot.dropItem(item, d.x, d.z, ey);
  }

  /** Nearest interactable (locked chest or door) within reach, or null. */
  /** A chest is spent once it's unlocked AND every slot has been emptied. */
  _chestEmpty(c) { return !!c.unlocked && (!c.contents || c.contents.every((s) => !s)); }

  _nearestInteract(reach = 2.6) {
    const px = this.player.pos.x, pz = this.player.pos.z;
    let best = null, bd = reach, kind = null;
    for (const c of (this.levelBuilder.lockedChests || [])) {
      if (this._chestEmpty(c)) continue;   // locked, or unlocked with loot still inside
      const d = Math.hypot(c.x - px, c.z - pz);
      if (d < bd) { bd = d; best = c; kind = "chest"; }
    }
    for (const dr of (this.levelBuilder.doors || [])) {
      if (dr.open) continue;
      const d = Math.hypot(dr.x - px, dr.z - pz);
      if (d < bd) { bd = d; best = dr; kind = "door"; }
    }
    return best ? { obj: best, kind } : null;
  }

  /** Open the nearest chest/door within reach (V). Locked ones cost Vault Keys.
   *  Re-pressing V while the chest screen is open closes it. */
  _tryInteract() {
    if (this.hud.chestOpen) { this.hud.closeChest(); return; }
    const near = this._nearestInteract();
    if (!near) return;
    if (near.kind === "chest") this._openLockedChest(near.obj);
    else if (near.obj.locked) this._openLockedDoor(near.obj);
    else this._openDoor(near.obj);
  }

  _openDoorMechanics(d) {
    d.open = true; d.opening = true;
    for (const [cx, cy] of d.cells) if (this.level.block[cy]) this.level.block[cy][cx] = 0;
    this.level.buildFlow(this.player.pos.x, this.player.pos.z);
  }

  _openDoor(d) {
    this._openDoorMechanics(d);
    this.effects.text(this.player.pos, "Door opened", "#c9a24b");
  }

  _openLockedDoor(d) {
    const cost = d.keyCost || 3;
    if ((this.player.keys || 0) < cost) {
      this.effects.text(this.player.pos, `Sealed — need ${cost} Vault Key${cost > 1 ? "s" : ""}`, "#ff8a7a");
      return;
    }
    this.player.keys -= cost;
    this._openDoorMechanics(d);
    this.effects.text(this.player.pos, "Vault door unsealed!", "#f0c34b");
    this.effects.burst({ x: d.x, y: 1.2, z: d.z }, "#f0c34b", 18);
    // the sealed room pays out a guaranteed gold hoard on the reward pedestal
    if (d.vault && this.level.vaultReward) {
      const w = this.level.gridToWorld(this.level.vaultReward.gx, this.level.vaultReward.gy);
      this._spawnLootBurst(rollChest("gold", this.floor + 2, 1.5), w.x, w.z);
    }
    this._autosave();
  }

  _openLockedChest(c) {
    // First time: pay keys, unlock, roll the 5-slot contents, swing the lid open.
    if (!c.unlocked) {
      const cost = c.keyCost || 1;
      if ((this.player.keys || 0) < cost) {
        this.effects.text(this.player.pos, `Locked — need ${cost} Vault Key${cost > 1 ? "s" : ""}`, "#ff8a7a");
        return;
      }
      this.player.keys -= cost;
      c.unlocked = true;
      if (c.lock && c.lock.parent) { c.lock.parent.remove(c.lock); c.lock = null; }
      if (c.mimic) { this._springMimic(c); return; }   // it's a trap!
      c.opening = true; c.openT = 0;
      c.contents = rollChestSlots(c.tier, this.floor);
      const label = c.tier ? c.tier.charAt(0).toUpperCase() + c.tier.slice(1) : "";
      this.effects.text(this.player.pos, `${label} vault unlocked!`, "#f0c34b");
      this.effects.burst({ x: c.x, y: 0.9, z: c.z }, "#f0c34b", 14);
      if (this.audio) this.audio.sfx("magic_charge", 0.5);
      this._autosave();
    }
    // Present the loot screen (both on first open and when returning for leftovers).
    this.hud.openChest(c);
  }

  /** A mimic chest springs into a monster on open. It drops its would-be hoard on
   *  death, so the key you spent still pays out — if you can win the fight. */
  _springMimic(c) {
    c.contents = [];                                   // no loot screen; it's empty as a chest
    if (c.obj && c.obj.parent) c.obj.parent.remove(c.obj);   // the chest "becomes" the mimic
    this.level.clearBlockerAt(c.x, c.z);
    const gy = this.level.groundY ? this.level.groundY(c.x, c.z) : 0;
    const e = new Enemy(this.engine.scene, this.registry, ENEMY_DEFS.Mimic, c.x, c.z, this.floor, this.theme.enemyKey, this.difficulty.statMult);
    e.groundH = gy; e.pos.y = gy; e.mesh.position.y = gy;
    e.mimicTier = c.tier || "bronze";                  // read by _onEnemyKilled to drop the hoard
    this.enemies.push(e);
    if (this.portal) { this.engine.scene.remove(this.portal.mesh); this.portal = null; }   // re-close a cleared portal
    this.effects.text(this.player.pos, "IT'S A MIMIC!", "#ff6a5a");
    this.effects.burst({ x: c.x, y: 0.9, z: c.z }, "#ff6a5a", 22);
    if (this.audio) this.audio.sfx("magic_charge", 0.7);
    this._autosave();
  }

  /** Called by the HUD when the player takes one item from an open chest.
   *  Coins/keys bank to their counters; gear/consumables go to the bag, or drop
   *  at the player's feet if the bag is full. The slot is ALWAYS emptied, so a
   *  taken item never lingers (no "won't clear" / infinite-take). */
  chestTake(c, idx) {
    if (!c || !c.contents) return "empty";
    const it = c.contents[idx];
    if (!it) return "empty";
    const status = this.godGive(it);        // banks coins/keys, bags, or drops at feet
    c.contents[idx] = null;                  // always clear the slot
    this._flashHud();
    this._autosave();
    return (typeof status === "string" && status.startsWith("dropped")) ? "dropped" : "took";
  }

  /** Rare Vault-Key finds lying in the dungeon (only fresh floors, not resumes;
   *  runs under the seeded RNG so a resume reproduces the same finds). */
  _scatterFloorKeys() {
    if (!GAME.lockedVaults || this.theme.id !== "dungeon") return;
    const roll = Math.random();
    const n = roll < 0.12 ? 2 : roll < 0.55 ? 1 : 0;   // ~55% one key, ~12% two
    if (!n) return;
    const cells = [];
    for (let y = 0; y < this.level.gridH; y++)
      for (let x = 0; x < this.level.gridW; x++)
        if (this.level.walkableGrid(x, y)) cells.push([x, y]);
    if (!cells.length) return;
    for (let i = 0; i < n; i++) {
      const [gx, gy] = cells[(Math.random() * cells.length) | 0];
      const w = this.level.gridToWorld(gx + 0.5, gy + 0.5);
      const yy = this.level.groundY ? this.level.groundY(w.x, w.z) : 0;
      this.loot.dropItem(makeKey(1), w.x, w.z, yy);
    }
  }

  /** Scatter a bundle of items as ground pickups around a point. */
  _spawnLootBurst(items, x, z) {
    for (const it of (items || [])) {
      if (!it) continue;
      const ox = x + (Math.random() * 1.6 - 0.8), oz = z + (Math.random() * 1.6 - 0.8);
      const yy = this.level.groundY ? this.level.groundY(ox, oz) : 0;
      this.loot.dropItem(it, ox, oz, yy);
    }
  }

  /** Bottom prompt when standing near a lockable chest/door in the dungeon. */
  _updateLockPrompt() {
    if (!this.hud.setLockPrompt) return;
    if (this.inHub || this.state !== "playing") { this.hud.setLockPrompt(null); return; }
    const near = this._nearestInteract();
    if (!near) { this.hud.setLockPrompt(null); return; }
    const o = near.obj;
    if (near.kind === "door" && !o.locked) { this.hud.setLockPrompt({ label: "Open door", keyCost: 0 }); return; }
    // already-unlocked chest → free to reopen for leftovers
    if (near.kind === "chest" && o.unlocked) { this.hud.setLockPrompt({ label: `Open ${(o.tier || "").toUpperCase()} chest`, keyCost: 0 }); return; }
    const cost = o.keyCost || 1;
    this.hud.setLockPrompt({
      label: near.kind === "chest" ? `${(o.tier || "").toUpperCase()} chest` : "Sealed vault door",
      keyCost: cost, have: this.player.keys || 0,
    });
  }

  _openPortal() {
    this.portal = createPortal(this.levelBuilder.portalPos);
    this.engine.scene.add(this.portal.mesh);
    this.effects.text(this.player.pos, "AREA CLEAR!", "#c9a24b");
    this._gainXp(20 + this.floor * 10);   // level-clear XP
    // Gear remembers surviving this depth; the Vault remembers you a little more.
    if (this.player) {
      const aW = recordDepth(this.player.weapon, this.player.classId);
      const aA = recordDepth(this.player.armor, this.player.classId);
      if (aW) this._relicWhisper(this.player.weapon, aW);
      if (aA) this._relicWhisper(this.player.armor, aA);
    }
    this.save.addNotoriety(2 + this.floor);
    this._noteDepth(); this._awardInsight(2);   // surviving a new depth teaches you
  }

  // ---------- physical hub: the Waystation (walk around, talk to NPCs) ----------
  _enterHub() {
    if (this.inHub) return;
    this.inHub = true; this.hubPanelOpen = false; this.state = "playing";
    this.audio.play("hub");   // Waystation music
    for (const e of this.enemies) e.dispose(); this.enemies = [];
    if (this._hazards) { this._hazards.dispose(); this._hazards = null; }
    this._clearTurrets();
    this.loot.clear(); this.combat.clearProjectiles();
    if (this.portal) { this.engine.scene.remove(this.portal.mesh); this.portal = null; }
    for (const n of this.npcs) n.dispose(); this.npcs = [];

    this.theme = THEMES.hub;
    this.level = this.theme.generator(this.theme);
    this.engine.applyEnvironment(this.theme.env);
    this.levelBuilder.build(this.level, this.theme);
    // endless grass plane under the town so there's no visible edge / abyss
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(400, 400), new THREE.MeshStandardMaterial({ color: 0x486a37, roughness: 1 }));
    ground.rotation.x = -Math.PI / 2; ground.position.y = -0.25; ground.receiveShadow = true;
    this.levelBuilder.group.add(ground);
    this._buildHexGround();          // real tessellated hex map (grass / road / water)
    this._buildBarrierDome();        // purple protective barrier around the town
    this._buildHelixMonument();      // the Helix of Restoration at the village centre
    this._buildHelixVeins();         // pulsing veins of power snaking across the ground
    if (!this.player) {              // resuming a saved run straight into the Waystation
      this.player = new Player(this.engine.scene, this.registry, this.level, this.character.model, this.character.id, this.profile ? this.profile.level : 1);
      this._applyPendingLoadout();
    }
    this.player.spawnAt(this.level);
    this._syncBagCap();
    this._spawnCompanions();

    // spawn the NPCs at their stations + block their tiles
    for (const d of (this.level.npcs || [])) {
      const w = this.level.gridToWorld(d.gx + 0.5, d.gy + 0.5);
      this.npcs.push(new Npc(this.engine.scene, this.registry, { ...d, x: w.x, z: w.z }));
      this.level.blockers.push({ x: w.x, z: w.z, r: 0.55 });
    }

    this.shopStock = makeShopStock(this.floor);
    this.homeRested = false;
    this.combat.reset([], this.player, this.level);
    this.combat.destructibles = this.levelBuilder.destructibles;
    this.minimap.reset(this.level);
    this._pcx = this._pcy = null;
    this.level.buildFlow(this.player.pos.x, this.player.pos.z);
    this.cameraRig.snapTo(this.player.pos.x, this.player.pos.z, this.player.groundH || 0);

    this.portal = createPortal(this.levelBuilder.portalPos);   // descend gate
    this.engine.scene.add(this.portal.mesh);
    this.effects.text(this.player.pos, "VILLAGE OF EDEN", "#cfe0ff");
    this._autosave();   // checkpoint at the Waystation
  }

  _handleHubInput() {
    const dragX = this.input.consumeDragX(); if (dragX) this.cameraRig.rotateBy(-dragX * 0.005);
    if (this.input.pressed("e")) { if (this.nearNpc) this._openStation(this.nearNpc.type); else if (this.nearHelix) this._openStation("helix"); else this.hud.toggleInventory(); }
    if (this.input.pressed("i")) this.hud.toggleInventory();
    if (this.input.pressed("h")) this._quickHeal();
    for (let i = 1; i <= 8; i++) if (this.input.pressed(String(i))) this._useSlot(i - 1);
  }

  _updateHub(dt) {
    // Re-pressing [E] while a station panel is open closes it (the key that
    // opened it also shuts it). The opening press was consumed the frame it
    // opened, so this won't fire until a fresh press.
    if (this.hubPanelOpen && this.input.pressed("e")) this.closeStation();
    if (!this.hubPanelOpen) {
      this._handleHubInput();
      this.player.update(dt, this.input, this.cameraRig, this.combat, this.level);
      this._footAudio(dt);
      // purple force field when pushing against the invisible town border
      this._barrierCd = Math.max(0, (this._barrierCd || 0) - dt);
      const k = this.input.keys;
      const f = this.cameraRig.forward, r = this.cameraRig.right;
      let mx = f.x * ((k["w"] ? 1 : 0) - (k["s"] ? 1 : 0)) + r.x * ((k["d"] ? 1 : 0) - (k["a"] ? 1 : 0));
      let mz = f.z * ((k["w"] ? 1 : 0) - (k["s"] ? 1 : 0)) + r.z * ((k["d"] ? 1 : 0) - (k["a"] ? 1 : 0));
      const len = Math.hypot(mx, mz);
      if (len > 0.01) {
        mx /= len; mz /= len;
        const blocked = this.level.collidesWorld(this.player.pos.x + mx * 0.7, this.player.pos.z + mz * 0.7, this.player.radius);
        const ahead = this.level.worldToGrid(this.player.pos.x + mx * 1.0, this.player.pos.z + mz * 1.0);
        const bx = Math.floor(ahead.gx), by = Math.floor(ahead.gy);
        const outsideTown = bx < 10 || bx > 35 || by < 7 || by > 28;   // the town edge, not a building
        if (blocked && outsideTown && this._barrierCd <= 0) {
          this._barrierCd = 0.1;
          this.effects.forceField(this.player.pos, mx, mz);
        }
      }
    }
    if (this._barrier) {
      const t = performance.now() * 0.001;
      this._barrier.tex.offset.x = (this._barrier.tex.offset.x + dt * 0.04) % 1;
      this._barrier.tex.offset.y = (this._barrier.tex.offset.y - dt * 0.015 + 1) % 1;
      this._barrier.mat.opacity = 0.34 + Math.sin(t * 1.4) * 0.12;
      this._barrier.cap.material.opacity = 0.16 + Math.sin(t * 1.4 + 0.8) * 0.07;
      this._barrier.mesh.rotation.y = Math.sin(t * 0.15) * 0.05;
    }
    if (this._helixFX) {
      const fx = this._helixFX; fx.t += dt;
      const arr = fx.pos, top = fx.base + fx.H + 0.6;
      for (let i = 0; i < fx.spd.length; i++) {
        let y = arr[i * 3 + 1] + dt * fx.spd[i];
        fx.ang[i] += dt * (0.35 + (i % 4) * 0.12);
        if (y > top) { y = fx.base - 0.2; }           // recycle to the base
        const r = fx.rad[i] + Math.sin(fx.t * 0.8 + i) * 0.06;
        arr[i * 3] = fx.cx + Math.cos(fx.ang[i]) * r;
        arr[i * 3 + 1] = y;
        arr[i * 3 + 2] = fx.cz + Math.sin(fx.ang[i]) * r;
      }
      fx.points.geometry.attributes.position.needsUpdate = true;
      const mon = this._helixMonument;
      if (mon) {
        const pulse = 0.5 + Math.sin(fx.t * 1.8) * 0.5;
        mon.auraMat.opacity = 0.10 + pulse * 0.08;
        mon.coreMat.opacity = 0.14 + pulse * 0.1;
        mon.strandMat.emissiveIntensity = 0.7 + pulse * 0.5;
        if (mon.light) mon.light.intensity = 1.2 + pulse * 0.9;
      }
    }
    if (this._helixVeins && !A11Y.reduceMotion) {   // reduced motion → hold the veins steady
      const t = performance.now() * 0.001;
      const hv = this._helixVeins;
      const W = 2.513;   // 2π / 2.5s → slow, ~2.5s pulse cycle
      // The painted LINES stay steady; only the key-point nodes breathe softly.
      for (const nd of hv.nodes) {
        const p = 0.5 + Math.sin(t * W + nd.phase) * 0.5;
        nd.glowMat.opacity = 0.25 + p * 0.45;
        const s = nd.baseSize * (1.3 + p * 0.55);   // subtle breathe, stays small
        nd.spr.scale.set(s, s, 1);
      }
      if (hv.sparkMat) hv.sparkMat.opacity = 0.3 + (0.5 + Math.sin(t * 1.7) * 0.5) * 0.35;   // gentle node twinkle
    }
    this.effects.update(dt);   // animate floating text / border flashes in the hub too
    this._updateCompanions(dt);
    for (const n of this.npcs) n.update(dt, this.player);
    let near = null, bd = 3.0;
    for (const n of this.npcs) { const d = Math.hypot(n.pos.x - this.player.pos.x, n.pos.z - this.player.pos.z); if (d < bd) { bd = d; near = n; } }
    this.nearNpc = near;
    // the central Helix monument is interactable when no NPC is closer
    let nh = false;
    if (!near && this._helixCenter) {
      const d = Math.hypot(this._helixCenter.x - this.player.pos.x, this._helixCenter.z - this.player.pos.z);
      if (d < 3.4) nh = true;
    }
    this.nearHelix = nh;
    this.hud.setInteractPrompt(!this.hubPanelOpen ? (near || (nh ? { type: "helix", name: "the Helix of Restoration" } : null)) : null);
    if (this.portal) {
      updatePortal(this.portal, dt, performance.now());
      if (!this.hubPanelOpen && Math.hypot(this.portal.pos.x - this.player.pos.x, this.portal.pos.z - this.player.pos.z) < 1.6) this.descend();
    }
    this.cameraRig.update(dt, this.player.pos);
    this.engine.focusLight(this.player.pos.x, this.player.pos.z);
    this.minimap.update(this.level, this.player, this.portal, this._hubMarkers());
    this.hud.update();
  }

  /** Minimap markers for the Village of Eden: each station NPC + the Helix. */
  _hubMarkers() {
    const STATION_COLOR = { blacksmith: "#e0975a", shop: "#ffd24a", home: "#7bd645", research: "#c77dff" };
    const out = [];
    for (const n of this.npcs) out.push({ x: n.pos.x, z: n.pos.z, color: STATION_COLOR[n.type] || "#cfe0ff" });
    if (this._helixCenter) out.push({ x: this._helixCenter.x, z: this._helixCenter.z, color: "#ffe8a3" });
    return out;
  }

  // Blacksmith prices — deliberately steep and escalating so upgrading is a
  // long-term investment, not a quick spend.
  forgeCost() {
    const f = this.floor, n = (this.player.weapon.forges || 0);
    return 320 + f * 160 + n * (500 + f * 120);          // each successive forge costs far more
  }
  enchantCost() {
    const f = this.floor, w = this.player.weapon;
    return 450 + f * 200 + Math.round((w.elementPower || 0) * 300);
  }
  reinforceCost() {
    const f = this.floor, a = this.player.armor;
    return 240 + f * 130 + Math.round((a.def || 0) * 34);
  }

  // ---------- Copper fuel (stored at the blacksmith; spent to forge/enchant) ----------
  copperReserve() { return this.meta.copper || 0; }
  _copperValue(it) { return (/bar/i.test(it.name) ? 3 : 1) * stackCount(it); }   // bars are worth more
  /** Deposit every copper resource in the bag into the blacksmith reserve. Returns fuel added. */
  depositCopper() {
    let added = 0;
    for (let i = 0; i < this.player.inv.length; i++) {
      const it = this.player.inv[i];
      if (!it || it.kind !== "resource" || !/copper/i.test(it.name)) continue;
      added += this._copperValue(it);
      this.player.inv[i] = null;
    }
    if (added) { this.meta.copper = (this.meta.copper || 0) + added; this.save.write(); }
    return added;
  }
  bagCopper() { return this.player.inv.reduce((n, it) => n + ((it && it.kind === "resource" && /copper/i.test(it.name)) ? this._copperValue(it) : 0), 0); }
  forgeCopperCost() { return 3 + (this.player.weapon.forges || 0) * 2; }   // escalates with each forge
  enchantCopperCost() { return 4; }
  _spendCopper(n) { if ((this.meta.copper || 0) < n) return false; this.meta.copper -= n; this.save.write(); return true; }

  _openStation(type) { this.hubPanelOpen = true; this.hud.openStation(type); }
  closeStation() { this.hubPanelOpen = false; this.hud.closeStation(); }

  // ---------- Home crafting (copper fuel + gold → items; gated by research) ----------
  recipes() { return RECIPES; }
  /** Whether a recipe is craftable right now: research owned + copper + gold + bag room. */
  canCraft(r) {
    if (!r || !this.player) return false;
    if (r.req && !this.hasResearch(r.req)) return false;
    if (this.copperReserve() < r.copper) return false;
    if ((this.player.gold || 0) < r.gold) return false;
    if (!r.banksKey && slotCount(this.player.inv) >= (this.player.invCap || 20)) return false;   // no bag room
    return true;
  }
  /** Craft a recipe by id. Returns { ok, msg }. */
  craft(id) {
    const r = recipeById(id); if (!r) return { ok: false, msg: "Unknown recipe." };
    const p = this.player; if (!p) return { ok: false, msg: "No hero." };
    if (r.req && !this.hasResearch(r.req)) return { ok: false, msg: "Research this recipe first." };
    if (this.copperReserve() < r.copper) return { ok: false, msg: `Need ${r.copper} copper fuel — deposit copper at the blacksmith.` };
    if ((p.gold || 0) < r.gold) return { ok: false, msg: `Need ${r.gold} gold.` };
    const item = r.make(this);
    if (r.banksKey || (item && item.kind === "key")) {
      p.keys = (p.keys || 0) + (item.amount || 1);
    } else {
      if (!canAdd(p.inv, item, p.invCap || 20)) return { ok: false, msg: "Bag full — make room first." };
      addItem(p.inv, item, p.invCap || 20);
    }
    this._spendCopper(r.copper);
    p.gold -= r.gold;
    this.save.write();
    return { ok: true, msg: `Crafted ${r.name}.` };
  }

  // ---------- Redemption codes (gift legendary weapons; one claim per save) ----------
  /** Redeem a UUID code for its gift weapon. Returns { ok, msg }. */
  redeemCode(raw) {
    const c = getCode(raw);
    if (!c) return { ok: false, msg: "That code isn't valid." };
    if (!this.meta.redeemed) this.meta.redeemed = {};
    if (this.meta.redeemed[c.key]) return { ok: false, msg: "This code has already been claimed." };
    const item = c.make();
    const p = this.player;
    let where = "your bag";
    if (p && canAdd(p.inv, item, p.invCap || 20)) addItem(p.inv, item, p.invCap || 20);
    else { addItem(this._stash(), item, null); where = "Home Storage"; }   // bag full → safe in the stash
    this.meta.redeemed[c.key] = Date.now();
    this.save.write();
    return { ok: true, msg: `✦ Claimed ${item.name}! It's in ${where}.` };
  }

  // ---------- Home storage (persistent stash, survives runs — UNLIMITED) ----------
  _stash() { if (!this.meta.stash) this.meta.stash = []; return this.meta.stash; }

  // ---------- Player bag capacity — expand it up to +10 by trading memories ----------
  bagCap() { return GAME.invCap + (this.meta.bagExpansions || 0); }
  bagExpansionsLeft() { return 10 - (this.meta.bagExpansions || 0); }
  _syncBagCap() { if (this.player) { this.player.invCap = this.bagCap(); padBag(this.player.inv, this.player.invCap); } }
  /** Trade one recovered memory for +1 permanent BAG slot (up to +10). */
  _spendOneMemory() {
    const keys = Object.keys(this.meta.codex || {}).filter((k) => this.meta.codex[k]);
    if (!keys.length) return false;
    delete this.meta.codex[keys[(Math.random() * keys.length) | 0]];
    return true;
  }
  expandBag() {
    if ((this.meta.bagExpansions || 0) >= 10) return { ok: false, msg: "Your bag is fully expanded (+10)." };
    if (!this._spendOneMemory()) return { ok: false, msg: "You need a recovered memory to trade." };
    this.meta.bagExpansions = (this.meta.bagExpansions || 0) + 1;
    this._syncBagCap();
    this.save.write();
    this._updateHelixMonument();   // spending a memory may un-light a codex line
    return { ok: true, cap: this.bagCap(), exp: this.meta.bagExpansions };
  }
  /** Deposit the whole stack at bag idx into the stash (unlimited). */
  depositToStash(idx) {
    const it = this.player.inv[idx]; if (!it || it.kind === "coins") return false;
    addItem(this._stash(), it, null);
    this.player.inv[idx] = null;
    this.save.write();
    return true;
  }
  /** Deposit a single unit from the bag stack at idx. */
  depositOneToStash(idx) {
    const it = this.player.inv[idx]; if (!it || it.kind === "coins") return false;
    const one = takeOne(this.player.inv, idx);
    addItem(this._stash(), one, null);
    this.save.write();
    return true;
  }
  depositAllToStash() {
    for (let i = 0; i < this.player.inv.length; i++) {
      const it = this.player.inv[i];
      if (!it || it.kind === "coins") continue;
      addItem(this._stash(), it, null);
      this.player.inv[i] = null;
    }
    this.save.write();
  }
  /** Withdraw the whole stack at stash idx into the bag (merges or takes a slot). */
  withdrawFromStash(idx) {
    const s = this._stash(); const it = s[idx]; if (!it) return false;
    const r = rehydrateItem({ ...it });
    if (!canAdd(this.player.inv, r, this.player.invCap || 20)) return false;   // no room
    addItem(this.player.inv, r, this.player.invCap || 20);
    s.splice(idx, 1);
    this.save.write();
    return true;
  }
  /** Withdraw a single unit from the stash stack at idx. */
  withdrawOneFromStash(idx) {
    const s = this._stash(); const it = s[idx]; if (!it) return false;
    const one = rehydrateItem({ ...it, qty: 1 });
    if (!canAdd(this.player.inv, one, this.player.invCap || 20)) return false;
    addItem(this.player.inv, one, this.player.invCap || 20);
    takeOne(s, idx, true);   // stash is packed → splice out
    this.save.write();
    return true;
  }
  /** Withdraw as many whole stacks as the bag has room for. Returns how many stacks moved. */
  withdrawAllFromStash() {
    const s = this._stash(); let n = 0;
    for (let i = s.length - 1; i >= 0; i--) {
      const r = rehydrateItem({ ...s[i] });
      if (!canAdd(this.player.inv, r, this.player.invCap || 20)) continue;   // no room for this stack
      addItem(this.player.inv, r, this.player.invCap || 20);
      s.splice(i, 1); n++;
    }
    if (n) this.save.write();
    return n;
  }

  // Procedural texture for the barrier shell: a soft vertical fade with faint
  // hex-cell shield lattice, tileable so it can scroll/shimmer.
  _barrierTex() {
    const c = document.createElement("canvas"); c.width = 256; c.height = 256;
    const g = c.getContext("2d");
    const grad = g.createLinearGradient(0, 256, 0, 0);   // bright at base → fade upward
    grad.addColorStop(0, "rgba(214,150,255,0.55)");
    grad.addColorStop(0.45, "rgba(199,125,255,0.26)");
    grad.addColorStop(1, "rgba(199,125,255,0.02)");
    g.fillStyle = grad; g.fillRect(0, 0, 256, 256);
    // hex lattice
    g.strokeStyle = "rgba(232,208,255,0.30)"; g.lineWidth = 2;
    const R = 20, h = R * Math.sqrt(3);
    for (let row = -1, y = 0; y < 256 + h; row++, y = row * h * 0.5) {
      const off = (row % 2) * R * 1.5;
      for (let x = -R; x < 256 + R; x += R * 3) this._hex(g, x + off, y, R);
    }
    // vertical energy streaks
    g.strokeStyle = "rgba(240,222,255,0.20)"; g.lineWidth = 3;
    for (let x = 18; x < 256; x += 64) { g.beginPath(); g.moveTo(x, 256); g.lineTo(x, 0); g.stroke(); }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(10, 2);
    return t;
  }

  _hex(g, cx, cy, R) {
    g.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = Math.PI / 6 + i * Math.PI / 3;
      const x = cx + Math.cos(a) * R, y = cy + Math.sin(a) * R;
      i ? g.lineTo(x, y) : g.moveTo(x, y);
    }
    g.closePath(); g.stroke();
  }

  // A translucent purple energy shell (wall + soft dome cap) that surrounds the
  // walkable town — the ward that keeps the reliks out of the Waystation.
  _buildBarrierDome() {
    const cx = 45, cz = 35, rx = 28, rz = 24, H = 11;
    const tex = this._barrierTex();
    const mat = new THREE.MeshBasicMaterial({
      map: tex, color: 0xc77dff, transparent: true, opacity: 0.36,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    });
    // vertical wall (open cylinder scaled to an ellipse)
    const wall = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, H, 72, 1, true), mat);
    wall.scale.set(rx, 1, rz);
    wall.position.set(cx, H / 2 - 0.5, cz);
    // soft dome cap so it reads as a protective bubble from the iso camera
    const capMat = new THREE.MeshBasicMaterial({
      color: 0xc77dff, transparent: true, opacity: 0.18,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    });
    const cap = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 24, 0, Math.PI * 2, 0, Math.PI * 0.5), capMat);
    cap.scale.set(rx, H * 0.62, rz);
    cap.position.set(cx, H - 0.5, cz);
    const group = new THREE.Group(); group.add(wall); group.add(cap);
    this.levelBuilder.group.add(group);
    this._barrier = { mesh: group, mat, tex, cap };
  }

  // The Helix of Restoration — a DNA-like double helix at the village centre.
  // Two glowing strands wound around a vertical axis, joined by 100 rungs (one
  // per section). Completed sections light with their tier colour.
  _buildHelixMonument() {
    const cx = 45, cz = 35;
    const N = HELIX_SECTIONS, H = 10, R = 1.15, turns = 5, base = 0.6;
    const g = new THREE.Group();
    // pedestal
    const ped = new THREE.Mesh(
      new THREE.CylinderGeometry(1.8, 2.3, 0.7, 28),
      new THREE.MeshStandardMaterial({ color: 0x271f3c, roughness: 0.7, metalness: 0.25 })
    );
    ped.position.set(cx, 0.15, cz); ped.receiveShadow = true; g.add(ped);
    // strand point arrays
    const aPts = [], bPts = [];
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * turns * Math.PI * 2;
      const y = base + (i / N) * H;
      aPts.push(new THREE.Vector3(cx + R * Math.cos(a), y, cz + R * Math.sin(a)));
      bPts.push(new THREE.Vector3(cx + R * Math.cos(a + Math.PI), y, cz + R * Math.sin(a + Math.PI)));
    }
    const strandMat = new THREE.MeshStandardMaterial({ color: 0x7a5fbf, emissive: 0x2a1a48, emissiveIntensity: 0.8, roughness: 0.35, metalness: 0.4 });
    const tubeA = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(aPts), 220, 0.08, 8, false), strandMat);
    const tubeB = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(bPts), 220, 0.08, 8, false), strandMat);
    g.add(tubeA); g.add(tubeB);
    // rungs (one per section)
    const up = new THREE.Vector3(0, 1, 0), rungs = [];
    for (let i = 0; i < N; i++) {
      const A = aPts[i], B = bPts[i];
      const mid = A.clone().add(B).multiplyScalar(0.5);
      const dir = B.clone().sub(A); const len = dir.length();
      const mat = new THREE.MeshStandardMaterial({ color: 0x241a34, emissive: 0x000000, roughness: 0.5 });
      const rung = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, len, 6), mat);
      rung.position.copy(mid);
      rung.quaternion.setFromUnitVectors(up, dir.clone().normalize());
      g.add(rung);
      rungs.push({ mat, section: i + 1 });
    }
    // ---- magical aura: a soft glowing column + rising motes of light ----
    const auraMat = new THREE.MeshBasicMaterial({
      color: 0xc77dff, transparent: true, opacity: 0.12,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    });
    const aura = new THREE.Mesh(new THREE.CylinderGeometry(2.0, 1.3, H + 2.0, 28, 1, true), auraMat);
    aura.position.set(cx, base + H / 2, cz);
    g.add(aura);
    // inner bright core
    const coreMat = new THREE.MeshBasicMaterial({ color: 0xe6d4ff, transparent: true, opacity: 0.16, blending: THREE.AdditiveBlending, depthWrite: false });
    const core = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, H, 12, 1, true), coreMat);
    core.position.set(cx, base + H / 2, cz);
    g.add(core);
    // a real point light so the monument casts a glow on the plaza (single, static)
    const light = new THREE.PointLight(0xc77dff, 1.5, 16, 2);
    light.position.set(cx, base + H * 0.5, cz);
    g.add(light);
    // rising particle motes swirling around the strands
    const COUNT = 240;
    const pos = new Float32Array(COUNT * 3);
    const spd = new Float32Array(COUNT), ang = new Float32Array(COUNT), rad = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      const a = Math.random() * Math.PI * 2, r = 0.35 + Math.random() * 1.9;
      ang[i] = a; rad[i] = r; spd[i] = 0.5 + Math.random() * 1.3;
      pos[i * 3] = cx + Math.cos(a) * r; pos[i * 3 + 1] = base + Math.random() * H; pos[i * 3 + 2] = cz + Math.sin(a) * r;
    }
    const pgeo = new THREE.BufferGeometry();
    pgeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const pMat = new THREE.PointsMaterial({
      color: 0xe0c8ff, size: 0.17, map: this._moteTex(), transparent: true, opacity: 0.9,
      blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
    });
    const points = new THREE.Points(pgeo, pMat);
    g.add(points);

    this.levelBuilder.group.add(g);
    this._helixMonument = { group: g, rungs, strandMat, aura, auraMat, core, coreMat, light };
    this._helixFX = { points, pos, spd, ang, rad, cx, cz, base, H, t: 0 };
    this.level.blockers.push({ x: cx, z: cz, r: 2.2 });   // walk around it
    this._helixCenter = { x: cx, z: cz };
    this._updateHelixMonument();
  }

  // Veins of power PAINTED INTO THE GROUND as a decal (not extruded tubes — those
  // read as plastic pipes). A canvas draws each vein flowing from the Helix out to
  // a key point (station NPCs + portal): a dark carved groove for depth, a soft
  // blurred purple glow that falls off, and a thin bright core. The decal is
  // static (the guide wants steady lines); only the glowing NODES at key points
  // pulse (~2.5s), with a few sparkles emitted at the nodes only.
  _buildHelixVeins() {
    const cx = 45, cz = 35;
    const group = new THREE.Group();
    const nodes = [], sparkClusters = [];

    // ---- key points the veins connect to: station NPCs + the portal ----
    const targets = [];
    for (const d of (this.level.npcs || [])) {
      const w = this.level.gridToWorld(d.gx + 0.5, d.gy + 0.5);
      targets.push({ x: w.x, z: w.z });
    }
    const pp = this.levelBuilder.portalPos;
    if (pp) targets.push({ x: pp.x, z: pp.z });

    if (targets.length) {
      // ---- decal bounds: box around the Helix + every key point, padded ----
      const all = [{ x: cx, z: cz }, ...targets];
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
      for (const p of all) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minZ = Math.min(minZ, p.z); maxZ = Math.max(maxZ, p.z); }
      const pad = 6; minX -= pad; maxX += pad; minZ -= pad; maxZ += pad;
      const w = maxX - minX, h = maxZ - minZ;
      // high-res canvas so the THIN lines stay crisp (~40 px/world-unit, capped)
      let cw = Math.round(w * 40), ch = Math.round(h * 40);
      const scl = Math.min(1, 2048 / Math.max(cw, ch)); cw = Math.round(cw * scl); ch = Math.round(ch * scl);
      const ppu = ((cw / w) + (ch / h)) / 2;
      const W2C = (x, z) => ({ x: (x - minX) / w * cw, y: (z - minZ) / h * ch });

      const cv = document.createElement("canvas"); cv.width = cw; cv.height = ch;
      const g2 = cv.getContext("2d");
      g2.lineCap = "round"; g2.lineJoin = "round";

      // ---- generate a BRANCHING crack network that flows to the key points ----
      // Main vein: Helix centre → a key point, gently wandering (sway 0 at ends).
      const wanderPath = (sx, sz, tx, tz) => {
        const dx = tx - sx, dz = tz - sz, len = Math.hypot(dx, dz) || 1;
        const px = -dz / len, pz = dx / len, n = Math.max(6, Math.round(len / 2.4));
        const amp = (Math.random() - 0.5) * len * 0.07;
        const pts = [];
        for (let s = 0; s <= n; s++) {
          const f = s / n, wob = Math.sin(f * Math.PI) * amp + (Math.random() - 0.5) * 0.35;
          pts.push({ x: sx + dx * f + px * wob, z: sz + dz * f + pz * wob });
        }
        return pts;
      };
      // A short, slightly jagged offshoot crack.
      const wanderCrack = (bx, bz, ang, len) => {
        const n = Math.max(3, Math.round(len / 1.4)); let x = bx, z = bz, a = ang; const pts = [{ x, z }];
        for (let s = 0; s < n; s++) { a += (Math.random() - 0.5) * 0.5; const st = len / n; x += Math.cos(a) * st; z += Math.sin(a) * st; pts.push({ x, z }); }
        return pts;
      };

      const veins = [];
      for (const t of targets) {
        const main = wanderPath(cx, cz, t.x, t.z); veins.push({ pts: main, w: 1 });
        const dist = Math.hypot(t.x - cx, t.z - cz);
        const nf = 2 + ((Math.random() * 2) | 0);
        for (let k = 0; k < nf; k++) {
          const idx = Math.max(1, Math.min(main.length - 2, (main.length * (0.3 + Math.random() * 0.5)) | 0));
          const b = main[idx], b2 = main[idx + 1] || b;
          const baseAng = Math.atan2(b2.z - b.z, b2.x - b.x);
          const ang = baseAng + (Math.random() < 0.5 ? 1 : -1) * (0.5 + Math.random() * 0.7);
          const flen = dist * (0.12 + Math.random() * 0.2);
          const fork = wanderCrack(b.x, b.z, ang, flen); veins.push({ pts: fork, w: 0.5 });
          if (Math.random() < 0.5) { const ff = fork[fork.length - 2] || fork[0]; veins.push({ pts: wanderCrack(ff.x, ff.z, ang + (Math.random() < 0.5 ? 1 : -1) * 0.7, flen * 0.5), w: 0.28 }); }
        }
      }
      for (let k = 0; k < 4; k++) veins.push({ pts: wanderCrack(cx, cz, Math.random() * 6.28, 3 + Math.random() * 3), w: 0.4 });

      // A stroke drawn in WORLD points, with a soft glow from shadowBlur.
      const strokeVein = (pts, width, color, alpha, blur, shadow) => {
        g2.globalAlpha = alpha; g2.strokeStyle = color; g2.lineWidth = Math.max(1, width);
        g2.shadowColor = shadow || color; g2.shadowBlur = blur || 0;
        const c = pts.map((p) => W2C(p.x, p.z));
        g2.beginPath(); g2.moveTo(c[0].x, c[0].y);
        for (let i = 1; i < c.length - 1; i++) { const mx = (c[i].x + c[i + 1].x) / 2, my = (c[i].y + c[i + 1].y) / 2; g2.quadraticCurveTo(c[i].x, c[i].y, mx, my); }
        g2.lineTo(c[c.length - 1].x, c[c.length - 1].y); g2.stroke();
      };
      // Three global passes so grooves sit under every glow: carved groove, then
      // soft purple glow (blur), then a thin bright-lavender core. THIN lines.
      g2.shadowBlur = 0;
      for (const v of veins) strokeVein(v.pts, ppu * 0.15 * v.w, "#140a24", 0.4, 0);
      for (const v of veins) strokeVein(v.pts, ppu * 0.08 * v.w, "#8a3ce0", 0.5, ppu * 0.5 * v.w, "#9a4cff");
      for (const v of veins) strokeVein(v.pts, ppu * 0.045 * v.w, "#e4ccff", 0.95, ppu * 0.26 * v.w, "#c77dff");
      g2.shadowBlur = 0; g2.globalAlpha = 1;

      // Small, subtle glow nodes baked at the key points + the Helix.
      const bakeNode = (x, z, rUnits) => {
        const c = W2C(x, z), r = rUnits * ppu;
        const grd = g2.createRadialGradient(c.x, c.y, 0, c.x, c.y, r);
        grd.addColorStop(0, "rgba(232,214,255,0.8)"); grd.addColorStop(0.4, "rgba(160,92,240,0.35)"); grd.addColorStop(1, "rgba(120,60,220,0)");
        g2.fillStyle = grd; g2.beginPath(); g2.arc(c.x, c.y, r, 0, 7); g2.fill();
      };
      for (const t of targets) bakeNode(t.x, t.z, 0.5);
      bakeNode(cx, cz, 0.95);

      const tex = new THREE.CanvasTexture(cv);
      tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4; tex.needsUpdate = true;
      const plane = new THREE.Mesh(new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
      plane.rotation.x = -Math.PI / 2; plane.position.set((minX + maxX) / 2, 0.04, (minZ + maxZ) / 2);
      plane.renderOrder = 2;
      group.add(plane);
    }

    // A small, slowly-pulsing glow node sitting on each key point (subtle).
    const addNode = (x, z, size) => {
      const glowMat = new THREE.SpriteMaterial({ map: this._moteTex(), color: 0xb673ff, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, depthWrite: false });
      const spr = new THREE.Sprite(glowMat); spr.position.set(x, 0.12, z);
      const s = size * 1.5; spr.scale.set(s, s, 1); group.add(spr);
      nodes.push({ glowMat, spr, baseSize: size, phase: Math.random() * 6.28 });
      for (let k = 0; k < 4; k++) sparkClusters.push({ x, z });
    };
    for (const t of targets) addNode(t.x, t.z, 0.5);
    addNode(cx, cz, 0.85);   // slightly larger pulsing node at the Helix base

    // ---- node sparkle field (clustered tightly at nodes only) ----
    const pos = new Float32Array(sparkClusters.length * 3);
    for (let i = 0; i < sparkClusters.length; i++) {
      const b = sparkClusters[i];
      pos[i * 3] = b.x + (Math.random() - 0.5) * 0.8;
      pos[i * 3 + 1] = 0.1 + Math.random() * 0.6;
      pos[i * 3 + 2] = b.z + (Math.random() - 0.5) * 0.8;
    }
    const sg = new THREE.BufferGeometry();
    sg.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const sparkMat = new THREE.PointsMaterial({
      color: 0xd0a6ff, size: 0.18, map: this._moteTex(), transparent: true, opacity: 0.6,
      blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
    });
    group.add(new THREE.Points(sg, sparkMat));

    this.levelBuilder.group.add(group);
    this._helixVeins = { group, nodes, sparkMat };
  }

  // Soft radial sprite for the helix light motes (built once, cached).
  _moteTex() {
    if (this._moteTexCache) return this._moteTexCache;
    const c = document.createElement("canvas"); c.width = c.height = 64;
    const x = c.getContext("2d");
    const grd = x.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, "rgba(255,255,255,1)");
    grd.addColorStop(0.35, "rgba(215,182,255,0.75)");
    grd.addColorStop(1, "rgba(215,182,255,0)");
    x.fillStyle = grd; x.fillRect(0, 0, 64, 64);
    this._moteTexCache = new THREE.CanvasTexture(c);
    return this._moteTexCache;
  }

  /** Recolour the helix rungs — a rung lights when its codex is fully collected. */
  _updateHelixMonument() {
    const mon = this._helixMonument;
    if (!mon || !this.inHub) return;
    const collected = this.meta.codex || {};
    for (const r of mon.rungs) {
      if (codexComplete(r.section, collected)) {
        const c = new THREE.Color(sectionTier(r.section).color);
        r.mat.color.copy(c); r.mat.emissive.copy(c).multiplyScalar(0.65);
      } else {
        r.mat.color.setHex(0x241a34); r.mat.emissive.setHex(0x000000);
      }
    }
    if (this.meta.helix && this.meta.helix.restored) { mon.strandMat.color.setHex(0xf0d68a); mon.strandMat.emissive.setHex(0x6a5a20); }
  }

  // Instance one hex tile model across placements, tile-top aligned to y=0.
  _instanceHex(key, placements) {
    if (!placements.length || !this.registry.has(key)) return;
    const src = this.registry.instance(key); src.updateWorldMatrix(true, true);
    let mesh = null; src.traverse((o) => { if (!mesh && o.isMesh) mesh = o; }); if (!mesh) return;
    const geo = mesh.geometry.clone(); geo.applyMatrix4(mesh.matrixWorld); geo.computeBoundingBox();
    const topY = geo.boundingBox.max.y;
    const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const inst = new THREE.InstancedMesh(geo, mat, placements.length);
    inst.receiveShadow = true; inst.castShadow = false;
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), p = new THREE.Vector3(), s = new THREE.Vector3(1, 1, 1);
    placements.forEach((pl, i) => { q.setFromAxisAngle(up, pl.rot || 0); p.set(pl.x, -topY, pl.z); m.compose(p, q, s); inst.setMatrixAt(i, m); });
    inst.instanceMatrix.needsUpdate = true;
    this.levelBuilder.group.add(inst);
  }

  // Lay a tessellated pointy-top hex map under the town: grass field, a road
  // path down the middle, and a small pond (blocked in the collision grid).
  _buildHexGround() {
    const cx = 45, cz = 35;                 // town centre (matches the walkable rect)
    const grass = [], water = [];
    // extend the field well past the walkable border so tiles run off-screen
    for (let r = -20; r <= 20; r++) for (let q = -24; q <= 24; q++) {
      const x = cx + 2.0 * (q + r / 2), z = cz + 1.732 * r;
      const pond = (r >= 8 && r <= 13 && q >= -15 && q <= -10);
      if (pond) water.push({ x, z });
      else grass.push({ x, z });
    }
    this._instanceHex("hex/tile_grass", grass);
    this._instanceHex("hex/tile_water", water);
    // block the collision cells under the pond
    for (const w of water) {
      const g = this.level.worldToGrid(w.x, w.z);
      const gx = Math.floor(g.gx), gy = Math.floor(g.gy);
      if (this.level.block[gy]) this.level.block[gy][gx] = 1;
    }
  }

  // ---------- debug helpers (localhost only) ----------
  debugStart(charId, biomeId) { this.debugBiome = biomeId || null; this.begin(charId || this.character.id); }
  debugGold(n = 1000) { if (this.player) this.player.gold = (this.player.gold || 0) + n; }
  /** Shop price of one Vault Key — climbs a little with depth. */
  keyPrice() { return 40 + Math.floor(this.floor || 1) * 10; }
  debugInsight(n = 50) { this.save.addInsight(n); this.meta.deepest = Math.max(this.meta.deepest || 0, 5); this.save.write(); }

  /** Toggle companions (pets) on/off live. */
  setFamiliar(on) {
    FEATURES.familiar = on;
    if (on) { this._spawnCompanions(); this._applyResearchToPlayer(); }
    else { this._disposeCompanions(); this._applyResearchToPlayer(); }
  }
  debugUnlockPets() { for (const p of PETS) this.save.unlockPet(p.id); this.meta.deepest = Math.max(this.meta.deepest || 0, 5); this.save.write(); }
  /** Toggle the Quaternius creature pack (used once monster models are wired). */
  setMonsters(on) { FEATURES.monsters = on; }
  debugHeal() { if (this.player) this.player.hp = this.player.maxHp; }
  debugToHub() { if (this.player && this.state === "playing" && !this.inHub) this._enterHub(); }
  debugNext() { if (this.inHub) this.descend(); else if (this.player && this.state === "playing") this._enterHub(); }
  debugReveal() {
    const v = this.minimap && this.minimap.visited;
    if (v) for (let y = 0; y < v.length; y++) v[y].fill(1);
  }
  debugKillAll() { for (const e of this.enemies) if (!e.dead) { e.dead = true; e.mesh.visible = false; } if (!this.inHub && !this.portal) this._openPortal(); }

  // ---------- God Menu spawners ----------
  /** Grant an item: coins→gold, keys→key count, otherwise bag (or drop at feet
   *  when the bag is full). Returns a short status string for the menu. */
  godGive(item) {
    if (!item || !this.player) return "no item";
    const p = this.player;
    if (item.kind === "coins") { p.gold = (p.gold || 0) + (item.amount || 0); this._flashHud(); return `+${item.amount} gold`; }
    if (item.kind === "key") { p.keys = (p.keys || 0) + (item.amount || 1); this._flashHud(); return `+${item.amount || 1} key`; }
    if (canAdd(p.inv, item, p.invCap || 20)) { addItem(p.inv, item, p.invCap || 20); this._flashHud(); return `bagged ${item.name}`; }
    // bag full → drop at the player's feet so nothing is lost
    const px = p.pos.x, pz = p.pos.z, y = p.groundH || 0;
    this.loot.dropItem(item, px + (Math.random() - 0.5), pz + (Math.random() - 0.5), y);
    return `dropped ${item.name} (bag full)`;
  }

  _flashHud() { if (this.hud) { this.hud._sig = ""; if (this.hud.inventoryOpen) this.hud.renderInventory(); this.hud.update && this.hud.update(); } }

  /** Give N Vault Keys directly. */
  godKeys(n = 1) { if (this.player) { this.player.keys = (this.player.keys || 0) + n; this._flashHud(); } }

  /** Spawn a live enemy by ENEMY_DEFS key near the player, registered for combat. */
  godSpawnEnemy(key) {
    if (!this.player || this.inHub || this.state !== "playing") return "not in a level";
    const def = ENEMY_DEFS[key]; if (!def) return "unknown enemy";
    const spot = this._scatterSpot({ x: this.player.pos.x, z: this.player.pos.z }, 4) || { x: this.player.pos.x + 2, z: this.player.pos.z };
    const e = new Enemy(this.engine.scene, this.registry, def, spot.x, spot.z, this.floor, this.theme.enemyKey, this.difficulty.statMult);
    e.groundH = this.level.groundY ? this.level.groundY(spot.x, spot.z) : 0;
    e.pos.y = e.groundH; e.mesh.position.y = e.groundH;
    this.enemies.push(e);   // combat.enemies is the same array reference → it goes live
    if (this.portal) { this.engine.scene.remove(this.portal.mesh); this.portal = null; }   // re-close a cleared portal
    if (this.effects) this.effects.text(this.player.pos, `Spawned ${def.name}`, "#ff8a5a");
    return `spawned ${def.name}`;
  }

  /** Spawn a Memory Fragment pickup at the player's feet. */
  godSpawnFragment() {
    if (!this.player) return "no player";
    const it = makeMemoryItem(rollMemory(this.meta.codex, this.meta.deepest));
    return this.godGive(it);
  }

  /** Give a named relic weapon (by id, or a random one). */
  godSpawnRelic(id) {
    const def = (id && NAMED_RELICS.find((r) => r.id === id)) || NAMED_RELICS[(Math.random() * NAMED_RELICS.length) | 0];
    return this.godGive(makeNamedRelic(def));
  }

  /** Leave the hub for the next dungeon floor. */
  descend() {
    for (const n of this.npcs) n.dispose(); this.npcs = [];
    this.inHub = false; this.hubPanelOpen = false; this.nearNpc = null;
    this.nearHelix = false; this._helixCenter = null; this._helixMonument = null; this._helixFX = null; this._helixVeins = null;
    this.hud.closeStation(); this.hud.setInteractPrompt(null);
    this.floor++;
    this._newFloor(false);
    this.hud.hideScreens();
    this.state = "playing";
    this.audio.play(this._boss ? "boss" : "game");   // boss battle theme on boss floors
    this._autosave();          // checkpoint at the start of the new floor
  }

  _onPlayerDeath() {
    if (this.state === "dead" || this.state === "menu" || this.state === "reviving") return;   // ignore repeat lethal hits
    this.state = "reviving";   // freeze the world behind the modal
    const have = totalCollected(this.meta.codex || {});
    const cost = have > 0 ? Math.min(1 + ((Math.random() * 3) | 0), have) : 0;   // randomized 1–3
    this.hud.showDeathModal({
      have, cost,
      onRevive: have > 0 ? () => this._revive(cost) : null,
      onNewRun: () => this._startOver(),
    });
  }

  /** End the run and go straight to Choose Your Hero to start over. */
  _startOver() {
    this.hud.hideDeathModal();
    const floor = this.floor;
    if (this.profile) { this.profile.runs++; if (floor > (this.profile.best || 0)) this.profile.best = floor; this.save.write(); }
    if (this.character) {
      this.save.recordShade({ name: this.character.name, hero: this.character.id, depth: floor, weapon: this.player && this.player.weapon ? this.player.weapon.name : null, at: Date.now() });
    }
    this.save.clearRun();
    this.hud.showCharacterSelect();   // tears down the dead run (enterCharSelect) + shows the picker
  }

  /** Spend `cost` random recovered memories to revive mid-run. */
  _revive(cost) {
    this.hud.hideDeathModal();
    const owned = Object.keys(this.meta.codex || {}).filter((k) => this.meta.codex[k]);
    for (let i = 0; i < cost && owned.length; i++) {
      const k = owned.splice((Math.random() * owned.length) | 0, 1)[0];
      delete this.meta.codex[k];      // the Vault takes back a memory as its price
    }
    this.save.write();
    const p = this.player;
    this.state = "playing";
    p.hp = Math.max(1, Math.round(p.maxHp * 0.6));
    p.invuln = 2.2;                    // brief grace so you don't instantly die again
    if (this.effects) { this.effects.text(p.pos, "THE VAULT RETURNS YOU", "#c77dff"); this.effects.ring(p.pos, "#c77dff", 4.5); this.effects.burst(p.pos, "#c77dff", 22); }
    for (const e of this.enemies) {    // shove nearby enemies back to give breathing room
      if (e.dead) continue;
      const dx = e.pos.x - p.pos.x, dz = e.pos.z - p.pos.z, d = Math.hypot(dx, dz) || 1;
      if (d < 4.5) { e.stagger = 1.0; const kx = e.pos.x + (dx / d) * 2.2, kz = e.pos.z + (dz / d) * 2.2; if (!this.level.collidesWorld(kx, kz, e.radius)) { e.pos.x = kx; e.pos.z = kz; } }
    }
    if (this.audio.current) this.audio.current.play?.().catch(() => {});
    this._autosave();
  }

  /** Return to the main landing/home screen (from pause). */
  returnToMenu() {
    this._teardownRun();
    this.charSelect = false; this._teardownCharPreview();
    if (!this.showcase) this._buildShowcase();
    this.state = "menu";
    this.audio.play("menu");
    this.hud.showStart();
  }

  /** The wisp auto-collected a pickup — surface it like a manual pickup. */
  _onFamiliarPickup(it) {
    if (!it) return;
    if (it.kind === "coins") this.effects.text(this.player.pos, `+${it.amount} gold`, "#f0c34b");
    else if (it.kind === "key") this.effects.text(this.player.pos, `🗝 +${it.amount || 1} Vault Key`, "#e6b24a");
    else if (it.kind === "fragment") this.effects.text(this.player.pos, "Memory Fragment", "#c9a24b");
    else this.effects.text(this.player.pos, it.name, it.rarity ? it.rarity.color : "#fff");
    if (this.hud.inventoryOpen) this.hud.renderInventory();
  }

  _useSlot(idx) {
    const it = this.player.inv[idx];
    if (!it) return;
    if (it.kind === "potion") { if (useItem(this.player, idx)) { const mana = it.potType === "mana" || it.mana; this.effects.text(this.player.pos, mana ? `+${it.mana} MP` : `+${it.heal}`, mana ? "#4aa3ff" : "#5fd35f"); this.audio.sfx(mana ? "magic_charge" : "magic_heal", 0.6); } }
    else if (it.kind === "food") { this.hud._eatFood(idx); return; }
    else if (it.kind === "fragment") {
      const fresh = this._collectMemory(it.fragId);
      takeOne(this.player.inv, idx);                 // consume one from the stack
      const forged = this._forgeCompletedCodices();
      let msg = fresh ? "Memory recovered" : "Memory already known";
      if (forged.length) msg = `Codex complete — Helix line ${forged[0].codexId} restored!`;
      this.effects.text(this.player.pos, msg, forged.length ? "#f0a63c" : "#c9a24b");
      this.hud._sig = ""; if (this.hud.inventoryOpen) this.hud.renderInventory();
      return;
    }
    else if (it.kind === "weapon" || it.kind === "armor") {
      const r = equipItem(this.player, idx);
      if (r && r.refused) { this.effects.text(this.player.pos, r.note, "#ff8a7a"); return; }
    }
    else return;
    this.hud._sig = "";
    if (this.hud.inventoryOpen) this.hud.renderInventory();
  }

  _quickHeal() {
    const idx = this.player.inv.findIndex((i) => i && i.kind === "potion" && i.potType !== "mana");
    if (idx >= 0) { const it = this.player.inv[idx]; useItem(this.player, idx); this.effects.text(this.player.pos, `+${it.heal}`, "#5fd35f"); this.hud._sig = ""; }
  }

  // ---------- Signature abilities (Q) ----------
  ability() { return this.character ? abilityFor(this.character.id) : null; }
  /** Ability rank from the Prowess research track (0–3). Higher = stronger + faster. */
  abilityRank() { return Math.max(0, Math.min(3, Math.round((this.researchMods().abilityRank) || 0))); }

  /** Fire the hero's signature ability if off cooldown and affordable (Q). */
  _useAbility() {
    const p = this.player; if (!p || this.inHub || this.state !== "playing") return;
    if (this.hud && (this.hud.chestOpen || this.hud.inventoryOpen)) return;   // not while a full-screen panel is open
    const a = this.ability(); if (!a) return;
    if (p.abilityCd > 0) return;                                   // on cooldown
    if ((a.mana || 0) > 0 && p.mana < a.mana) { this.effects.text(p.pos, "Not enough mana", "#7db0ff"); p.outOfMana = 0.4; return; }
    if ((a.mana || 0) > 0) p.mana -= a.mana;
    const rank = this.abilityRank();
    p.abilityCd = a.cd * (1 - 0.12 * rank); p.abilityCdMax = p.abilityCd;   // Prowess shortens the cooldown
    const f = this.floor || 1, pw = (p.weaponPower ? p.weaponPower() : 1) * (1 + 0.22 * rank);   // …and boosts potency
    if (a.id === "bulwark") this._abBulwark(p, f, pw, rank);
    else if (a.id === "whirlwind") this._abWhirlwind(p, f, pw, rank);
    else if (a.id === "smoke") this._abSmoke(p, f, rank);
    else if (a.id === "turret") this._abTurret(p, f, pw, rank);
    else if (a.id === "nova") this._abNova(p, f, pw, rank);
    if (this.audio) this.audio.sfx("magic_charge", 0.6);
  }

  _abBulwark(p, f, pw, rank = 0) {
    const dmg = Math.round((16 + f * 3) * pw);
    const r = 3.0 + rank * 0.3;
    this.combat.aoeDamage(p.pos.x, p.pos.z, r, dmg, "#8fb8ff", "stun");
    this.effects.ring({ x: p.pos.x, y: 0.1, z: p.pos.z }, "#8fb8ff", r + 0.2);
    this.effects.burst({ x: p.pos.x, y: 0.6, z: p.pos.z }, "#cfe0ff", 20);
    p.shieldTimer = 4.5 + rank * 1.0;                              // halve incoming, longer with rank
    if (rank >= 3) { p.hp = Math.min(p.maxHp, p.hp + Math.round(p.maxHp * 0.15)); this.effects.text(p.pos, "AEGIS", "#bfe0ff"); }   // ★ ultimate: heal
    else this.effects.text(p.pos, "BULWARK", "#8fb8ff");
  }

  _abWhirlwind(p, f, pw, rank = 0) {
    p.whirl = { t: 1.6 + rank * 0.3, tick: 0, dmg: Math.round((9 + f * 2) * pw), pull: rank >= 3 };   // ★ ultimate: pulls foes in
    this.effects.text(p.pos, rank >= 3 ? "CYCLONE" : "WHIRLWIND", "#e0a15a");
  }

  _abSmoke(p, f, rank = 0) {
    p.invuln = Math.max(p.invuln || 0, 2.4 + rank * 0.4);          // untouchable, longer with rank
    p.hasteTimer = 2.6 + rank * 0.4;                               // and fast
    const r = 3.8 + rank * 0.4;
    this.effects.ring({ x: p.pos.x, y: 0.1, z: p.pos.z }, "#c9cdd6", r - 0.2);
    for (let i = 0; i < 3; i++) this.effects.burst({ x: p.pos.x, y: 0.7, z: p.pos.z }, "#b9bec7", 16);
    for (const e of this.enemies) {
      if (e.dead || Math.hypot(e.pos.x - p.pos.x, e.pos.z - p.pos.z) > r) continue;
      e.applySlow(0.5, 3.2);
      if (rank >= 3) e.applyPoison(3 + f * 0.5, 4);                // ★ ultimate: choking fog (DoT)
    }
    this.effects.text(p.pos, rank >= 3 ? "CHOKING FOG" : "VANISH", "#c9cdd6");
  }

  _abTurret(p, f, pw, rank = 0) {
    const gy = this.level.groundY ? this.level.groundY(p.pos.x, p.pos.z) : 0;
    const opts = { life: 8 + rank * 1.5, range: 11 + rank, fireRate: Math.max(0.3, 0.6 - rank * 0.08), dmg: Math.round((8 + f * 2) * pw) };
    this.turrets.push(new Turret(this.engine.scene, p.pos.x, p.pos.z, gy, opts));
    if (rank >= 3) {   // ★ ultimate: twin sentries flanking the deploy point
      const off = 1.2;
      this.turrets.push(new Turret(this.engine.scene, p.pos.x + off, p.pos.z, gy, opts));
      this.turrets.push(new Turret(this.engine.scene, p.pos.x - off, p.pos.z, gy, opts));
    }
    this.effects.text(p.pos, rank >= 3 ? "TWIN SENTRIES" : "SENTRY DEPLOYED", "#caff8a");
  }

  _abNova(p, f, pw, rank = 0) {
    const dmg = Math.round((20 + f * 4) * pw);
    const r = 4.2 + rank * 0.5;
    this.combat.aoeDamage(p.pos.x, p.pos.z, r, dmg, "#c77dff", "burn");
    if (rank >= 3) for (const e of this.enemies) { if (!e.dead && Math.hypot(e.pos.x - p.pos.x, e.pos.z - p.pos.z) <= r) e.applyStun(0.8); }   // ★ ultimate: also stuns
    this.effects.ring({ x: p.pos.x, y: 0.1, z: p.pos.z }, "#c77dff", r + 0.2);
    this.effects.ring({ x: p.pos.x, y: 0.1, z: p.pos.z }, "#e4ccff", 2.4);
    this.effects.burst({ x: p.pos.x, y: 0.8, z: p.pos.z }, "#e4ccff", 26);
    this.effects.text(p.pos, rank >= 3 ? "CATACLYSM" : "ARCANE NOVA", "#c77dff");
  }

  /** Per-frame ability upkeep: Whirlwind damage pulses + turret ticking. */
  _updateAbilityFx(dt) {
    const p = this.player;
    if (p && p.whirl) {
      p.whirl.t -= dt; p.whirl.tick -= dt;
      // ★ Cyclone (rank 3): drag nearby foes toward the eye of the storm
      if (p.whirl.pull) {
        for (const e of this.enemies) {
          if (e.dead) continue;
          const dx = p.pos.x - e.pos.x, dz = p.pos.z - e.pos.z, d = Math.hypot(dx, dz);
          if (d > 0.6 && d < 4.5) {
            const nx = e.pos.x + (dx / d) * 2.6 * dt, nz = e.pos.z + (dz / d) * 2.6 * dt;
            if (!this.level.collidesWorld(nx, nz, e.radius)) { e.pos.x = nx; e.pos.z = nz; }
          }
        }
      }
      if (p.whirl.tick <= 0) {
        p.whirl.tick = 0.22;
        this.combat.aoeDamage(p.pos.x, p.pos.z, 2.7, p.whirl.dmg, "#e0a15a");
        this.effects.ring({ x: p.pos.x, y: 0.1, z: p.pos.z }, "#e0a15a", 2.7);
      }
      if (p.whirl.t <= 0) p.whirl = null;
    }
    if (this.turrets.length) {
      for (const t of this.turrets) t.update(dt, this.combat);
      this.turrets = this.turrets.filter((t) => !t.dead);
    }
  }

  _clearTurrets() {
    for (const t of this.turrets) t.dispose();
    this.turrets = [];
    // also drop any in-flight ability state so a channel/buff can't bleed across floors
    const p = this.player;
    if (p) { p.whirl = null; p.shieldTimer = 0; p.hasteTimer = 0; p.abilityCd = 0; }
  }

  _handleInput() {
    const dragX = this.input.consumeDragX();          // middle-drag spins the camera
    if (dragX) this.cameraRig.rotateBy(-dragX * 0.005);
    if (this.input.pressed("e") || this.input.pressed("i")) this.hud.toggleInventory();
    if (this.input.pressed("h")) this._quickHeal();
    if (this.input.pressed("p")) this.toggleWisp();
    if (this.input.pressed("q")) this._useAbility();
    if (this.input.pressed("v")) this._tryInteract();
    for (let i = 1; i <= 8; i++) if (this.input.pressed(String(i))) this._useSlot(i - 1);
    if (this.input.pressed("f")) {
      const it = this.loot.tryPickup(this.player);
      if (it && it.full) this.effects.text(this.player.pos, "Bag full!", "#ff8a7a");
      else if (it && it.kind === "fragment") {
        this.effects.text(this.player.pos, "Memory Fragment recovered — turn it in at Home", "#c9a24b");
      }
      else if (it) this.effects.text(this.player.pos, it.kind === "coins" ? `+${it.amount} gold` : it.kind === "key" ? `🗝 +${it.amount || 1} Vault Key` : "Picked up " + it.name, it.rarity ? it.rarity.color : "#fff");
      if (this.hud.inventoryOpen) this.hud.renderInventory();
    }
  }

  /** Play the equipped weapon's sound on the rising edge of an attack swing. */
  _attackAudio() {
    const s = this.player ? this.player.swing : 0;
    if (s > 0 && !(this._prevSwing > 0)) this.audio.weaponSound(this.player.weapon);
    this._prevSwing = s;
  }

  /** Cadence footsteps while the player is moving (block-heel in the dungeon). */
  _footAudio(dt) {
    this._stepT = (this._stepT || 0) - dt;
    if (this.player && this.player.moving && !this.hubPanelOpen) {
      if (this._stepT <= 0) {
        this.audio.footstep(this.theme ? this.theme.id : "dungeon");
        this._stepT = this.player.rollTimer > 0 ? 0.45 : 0.33;
      }
    } else if (this._stepT < 0) this._stepT = 0;
  }

  _update(dt) {
    if (this.state !== "playing") return;
    this._handleInput();
    this.player.update(dt, this.input, this.cameraRig, this.combat, this.level);
    this._attackAudio(); this._footAudio(dt);
    // periodic autosave (covers mid-floor inventory/gold/hp changes)
    this._asT = (this._asT || 0) - dt;
    if (this._asT <= 0) { this._asT = 20; this._autosave(); }
    if (this.godMode) this.player.hp = this.player.maxHp;   // debug
    { // rebuild the flow field only when the player enters a new cell
      const g = this.level.worldToGrid(this.player.pos.x, this.player.pos.z);
      const cx = Math.floor(g.gx), cy = Math.floor(g.gy);
      if (cx !== this._pcx || cy !== this._pcy) { this._pcx = cx; this._pcy = cy; this.level.buildFlow(this.player.pos.x, this.player.pos.z); }
    }
    this.combat.buildEnemyGrid();   // spatial hash so each enemy separates against neighbours, not all
    for (const e of this.enemies) e.update(dt, this.player, this.level, this.combat);
    this.combat.update(dt);
    this._updateAbilityFx(dt);      // Whirlwind pulses + Ranger turrets
    this.loot.update(dt);
    this.effects.update(dt);
    this._updateCompanions(dt);
    // raise opening portcullis doors
    for (const d of (this.levelBuilder.doors || [])) {
      if (!d.opening) continue;
      d.mesh.position.y = Math.min(d.openY, d.mesh.position.y + dt * 3.2);
      if (d.mesh.position.y >= d.openY - 0.01) d.opening = false;
    }
    // locked chests: spin the lock glow (closed) / hinge the lid (opening)
    for (const c of (this.levelBuilder.lockedChests || [])) {
      if (c.opening) {
        c.openT = Math.min(1, (c.openT || 0) + dt * 3);
        if (c.lid) c.lid.rotation.x = -2.0 * c.openT;
        if (c.openT >= 1) c.opening = false;
      } else if (!c.unlocked && c.lock) { c.lock.rotation.y += dt * 1.6; }
    }
    this._updateLockPrompt();
    if (this.portal) {
      updatePortal(this.portal, dt, performance.now());
      if (Math.hypot(this.portal.pos.x - this.player.pos.x, this.portal.pos.z - this.player.pos.z) < 1.6) this._enterHub();
    }
    this.cameraRig.update(dt, this.player.pos);
    this.engine.focusLight(this.player.pos.x, this.player.pos.z);
    if (this._hazards && this.player.hp > 0) { if (this._hazards.update(dt, this.player, this.effects, this.combat)) this._onPlayerDeath(); }
    this.minimap.update(this.level, this.player, this.portal);
    this.hud.update();
  }

  _loop() {
    const frame = () => {
      try {
        const dt = clamp(this.engine.clock.getDelta(), 0, 0.05);
        if (this.state === "paused" || this.state === "reviving" || this._recapOpen || (this.hud && this.hud.chestOpen)) { /* frozen — just keep rendering */ }
        else if (this.state === "menu") { if (this.charSelect) this._updateCharPreview(dt); else this._updateShowcase(dt); }
        else if (this.inHub) this._updateHub(dt);
        else this._update(dt);
        this.engine.render(this.cameraRig.camera);
        this.input.endFrame();
      } catch (err) {
        // Never let one bad frame kill the loop with a blank screen.
        console.error("[Relikara] frame error:", err);
        try { this.save.flush(); } catch (_) {}
        if (!this._errShown && this.hud) { this._errShown = true; this.hud.toast("Something went wrong — your run is saved. Reload if it persists.", 6000); }
      }
      requestAnimationFrame(frame);
    };
    frame();
  }
}
